/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * MailCraft API — email automation, templates, AI generation, send logs.
 * OpenAPI spec version: 0.1.0
 */
export type SenderConfigStatus =
  (typeof SenderConfigStatus)[keyof typeof SenderConfigStatus];

export const SenderConfigStatus = {
  verified: "verified",
  unverified: "unverified",
  unknown: "unknown",
} as const;

export type EmailValidationVerdictStatus =
  (typeof EmailValidationVerdictStatus)[keyof typeof EmailValidationVerdictStatus];

export const EmailValidationVerdictStatus = {
  valid: "valid",
  risky: "risky",
  invalid: "invalid",
} as const;

/**
 * Normalized verdict from the Abstract API email-validation check. `status` is one of `valid` / `risky` / `invalid`. The `reasons` array carries stable codes — `disposable`, `role`, `undeliverable`, `bad_format`, `no_mx`, `risky`, `unknown`, `smtp_uncertain`, `validator_unavailable`, `empty` — that the UI can branch on without parsing free-form text. When the validator failed open (key missing or Abstract unreachable), the verdict is `valid` with `validator_unavailable` in `reasons`.

 */
export interface EmailValidationVerdict {
  status: EmailValidationVerdictStatus;
  reasons: string[];
}

/**
 * Verification status of the configured `MAILCRAFT_FROM_EMAIL` sender, determined at server boot by checking against Resend's verified domains. `verified` means the address can actually deliver. `unverified` means a free-mail provider host or a host that Resend does not have verified — sends will be rejected. `unknown` means the API could not query Resend's domain list (e.g. network failure) so verification cannot be confirmed; sends may still fail.
`validation` is the deliverability verdict for the sender address itself, produced by the same `EmailValidationVerdict` pipeline used for recipients. It is `null` when validation has not run yet (e.g. fail-open path during boot). A `risky` or `invalid` verdict surfaces as a dismissable warning on the sender-health card so a typo or stale-domain sender is caught early instead of silently degrading deliverability.

 */
export interface SenderConfig {
  address: string;
  status: SenderConfigStatus;
  /** @nullable */
  reason: string | null;
  validation: null | EmailValidationVerdict;
}

export interface HealthStatus {
  status: string;
  senderConfig: SenderConfig;
}

export interface ErrorResponse {
  error: string;
}

/**
 * The user's preferred lookback window (in days) for the verification-stats panel on the inbox page. Default 7.

 */
export type UserStatsLookbackDays =
  (typeof UserStatsLookbackDays)[keyof typeof UserStatsLookbackDays];

export const UserStatsLookbackDays = {
  NUMBER_7: 7,
  NUMBER_14: 14,
  NUMBER_30: 30,
  NUMBER_90: 90,
} as const;

/**
 * The user's preferred read/unread filter for the inbox page. Synced across devices. Default "all".

 */
export type UserInboxReadFilter =
  (typeof UserInboxReadFilter)[keyof typeof UserInboxReadFilter];

export const UserInboxReadFilter = {
  all: "all",
  unread: "unread",
  read: "read",
} as const;

/**
 * The user's preferred view filter (active / snoozed / pinned) for the inbox page. Synced across devices. Default "active".

 */
export type UserInboxViewFilter =
  (typeof UserInboxViewFilter)[keyof typeof UserInboxViewFilter];

export const UserInboxViewFilter = {
  active: "active",
  snoozed: "snoozed",
  pinned: "pinned",
} as const;

export interface User {
  id: number;
  clerkUserId: string;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  imageUrl?: string | null;
  /** True when the signed-in user's email is listed in the MAILCRAFT_ADMIN_EMAILS env var. Admins can soft-hide built-in templates from every user, and hard-delete templates owned by other users. Synced on every /me call.
   */
  isAdmin: boolean;
  /**
   * User's saved default From display name for the compose page. When set, replaces the brand-wide From-name default for this user only. Editable by all users via PATCH /me/preferences.

   * @nullable
   */
  defaultFromName?: string | null;
  /**
   * User's saved default From local-part (the part before `@cloudaiassistance.com`). When set, replaces the brand-wide From-alias default for this user only. Editable by all users via PATCH /me/preferences. Validated against the same regex as the per-send override (1-64 lowercase letters, digits, ".", "_", "-"; must start and end with a letter or digit).

   * @nullable
   */
  defaultFromLocalPart?: string | null;
  /** When true, opening a Gmail message in MailCraft auto-clears the `UNREAD` label on the Gmail side (using the existing `gmail.modify` scope). Default false so existing users who installed the read-only integration aren't surprised by their Gmail unread counts changing. The explicit Mark read / Mark unread toggle on the detail page works independently of this preference.
   */
  gmailMarkReadOnView: boolean;
  /** When true, MailCraft will not send early-warning emails when a webhook endpoint starts failing (1st and 5th consecutive failure). The auto-pause notification is always sent regardless of this setting. Default false.
   */
  webhookFailureEmailsDisabled: boolean;
  /** The user's preferred lookback window (in days) for the verification-stats panel on the inbox page. Default 7.
   */
  statsLookbackDays: UserStatsLookbackDays;
  /** The user's preferred read/unread filter for the inbox page. Synced across devices. Default "all".
   */
  inboxReadFilter: UserInboxReadFilter;
  /** The user's preferred view filter (active / snoozed / pinned) for the inbox page. Synced across devices. Default "active".
   */
  inboxViewFilter: UserInboxViewFilter;
  createdAt: string;
}

/**
 * Set the user's preferred lookback window (in days) for the verification-stats panel. Omit to leave unchanged; pass `null` to reset to the default (7).

 * @nullable
 */
export type UpdateMePreferencesInputStatsLookbackDays =
  | (typeof UpdateMePreferencesInputStatsLookbackDays)[keyof typeof UpdateMePreferencesInputStatsLookbackDays]
  | null;

export const UpdateMePreferencesInputStatsLookbackDays = {
  NUMBER_7: 7,
  NUMBER_14: 14,
  NUMBER_30: 30,
  NUMBER_90: 90,
} as const;

/**
 * Set the user's preferred inbox read filter. Omit to leave unchanged; pass `null` to reset to the default ("all").

 * @nullable
 */
export type UpdateMePreferencesInputInboxReadFilter =
  | (typeof UpdateMePreferencesInputInboxReadFilter)[keyof typeof UpdateMePreferencesInputInboxReadFilter]
  | null;

export const UpdateMePreferencesInputInboxReadFilter = {
  all: "all",
  unread: "unread",
  read: "read",
} as const;

/**
 * Set the user's preferred inbox view filter. Omit to leave unchanged; pass `null` to reset to the default ("active").

 * @nullable
 */
export type UpdateMePreferencesInputInboxViewFilter =
  | (typeof UpdateMePreferencesInputInboxViewFilter)[keyof typeof UpdateMePreferencesInputInboxViewFilter]
  | null;

export const UpdateMePreferencesInputInboxViewFilter = {
  active: "active",
  snoozed: "snoozed",
  pinned: "pinned",
} as const;

/**
 * Per-user preference patch. Pass `null` (or omit) for any field to leave it unchanged on the server side; pass an empty string to clear the saved value and fall back to the brand-wide default.

 */
export interface UpdateMePreferencesInput {
  /**
   * @maxLength 120
   * @nullable
   */
  defaultFromName?: string | null;
  /**
   * @maxLength 64
   * @nullable
   */
  defaultFromLocalPart?: string | null;
  /**
   * Toggle the per-user "auto-mark Gmail messages read on view" preference. Pass `null` (or omit) to leave the saved value unchanged.

   * @nullable
   */
  gmailMarkReadOnView?: boolean | null;
  /**
   * Toggle the per-user "suppress webhook failure warning emails" preference. Omit to leave unchanged; pass `null` to reset to the default (false); `true` to suppress; `false` to re-enable.

   * @nullable
   */
  webhookFailureEmailsDisabled?: boolean | null;
  /**
   * Set the user's preferred lookback window (in days) for the verification-stats panel. Omit to leave unchanged; pass `null` to reset to the default (7).

   * @nullable
   */
  statsLookbackDays?: UpdateMePreferencesInputStatsLookbackDays;
  /**
   * Set the user's preferred inbox read filter. Omit to leave unchanged; pass `null` to reset to the default ("all").

   * @nullable
   */
  inboxReadFilter?: UpdateMePreferencesInputInboxReadFilter;
  /**
   * Set the user's preferred inbox view filter. Omit to leave unchanged; pass `null` to reset to the default ("active").

   * @nullable
   */
  inboxViewFilter?: UpdateMePreferencesInputInboxViewFilter;
}

export type TemplateAutoDefaultsValues = { [key: string]: string };

/**
 * Auto-generated initial values for variables on the given template whose `autoGenerate` descriptor is set. Map keys are variable keys (e.g. `caseReference`); values are the reference strings. Only auto-generated variables appear here — static defaults are already in the template payload.

 */
export interface TemplateAutoDefaults {
  values: TemplateAutoDefaultsValues;
}

export interface AdminUser {
  /** Local users.id, or null if the user has not yet hit any authenticated route. */
  id: number | null;
  clerkUserId: string;
  /** @nullable */
  email: string | null;
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
  /** @nullable */
  imageUrl: string | null;
  /** Effective admin status (env allow-list OR dashboard toggle). */
  isAdmin: boolean;
  /** True when the user's email is in MAILCRAFT_ADMIN_EMAILS, in which case the toggle cannot demote them. */
  isAdminLockedByEnv: boolean;
  createdAt: string;
  /** @nullable */
  lastSignInAt: string | null;
}

export interface AdminUserList {
  users: AdminUser[];
  totalCount: number;
}

export interface AdminCreateUserInput {
  emailAddress: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /**
   * Optional initial password. When omitted, the user is created without one and must use a magic-link / OAuth on first sign-in.
   * @minLength 8
   * @nullable
   */
  password?: string | null;
  /** Whether this user starts as an admin. */
  isAdmin?: boolean;
}

export type AdminBulkDeleteInputConfirmation =
  (typeof AdminBulkDeleteInputConfirmation)[keyof typeof AdminBulkDeleteInputConfirmation];

export const AdminBulkDeleteInputConfirmation = {
  DELETE: "DELETE",
} as const;

export interface AdminBulkDeleteInput {
  /**
   * @minItems 1
   * @maxItems 100
   */
  clerkUserIds: string[];
  confirmation: AdminBulkDeleteInputConfirmation;
}

export interface AdminMergeInput {
  destinationEmail: string;
  sourceEmailConfirmation: string;
}

export interface AdminUpdateUserInput {
  /** Admin-authorized replacement primary email; preserves account identity and data. Existing accounts must be merged instead. */
  emailAddress?: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** Toggle local admin flag. Ignored when the user's email is locked by MAILCRAFT_ADMIN_EMAILS. */
  isAdmin?: boolean;
}

/**
 * Owning user of a trashed template, attached to admin trash listings so the admin can identify whose template they're about to restore. All identifying fields can be null (e.g. when the Clerk profile has been deleted but the local row is still referenced).

 */
export interface AdminTrashedTemplateOwner {
  id: number;
  /** @nullable */
  clerkUserId: string | null;
  /** @nullable */
  email: string | null;
  /** @nullable */
  firstName: string | null;
  /** @nullable */
  lastName: string | null;
}

export type TemplateLayout =
  (typeof TemplateLayout)[keyof typeof TemplateLayout];

export const TemplateLayout = {
  minimal: "minimal",
  corporate: "corporate",
  modern_saas: "modern_saas",
  financial: "financial",
  ecommerce: "ecommerce",
  alert: "alert",
} as const;

export type TemplateVariableType =
  (typeof TemplateVariableType)[keyof typeof TemplateVariableType];

export const TemplateVariableType = {
  text: "text",
  textarea: "textarea",
  url: "url",
  email: "email",
  number: "number",
  date: "date",
} as const;

/**
 * Marks the variable's initial value as coming from a persistent counter rather than the static `defaultValue`. The compose page calls `GET /templates/{id}/auto-defaults` on load and pre-fills this field with the next reference string.

 * @nullable
 */
export type TemplateVariableAutoGenerate = {
  kind: "sequence";
  /**
   * Counter key. Templates that share a prefix share the same counter (e.g. `PYPL-FRD-` is shared by every PayPal fraud template).

   * @minLength 1
   */
  prefix: string;
  /**
   * The smallest value the counter will ever return. The first peek creates the counter row at this value.

   * @minimum 0
   */
  startAt: number;
} | null;

export interface TemplateVariable {
  key: string;
  label: string;
  type: TemplateVariableType;
  required: boolean;
  /** @nullable */
  defaultValue?: string | null;
  /** @nullable */
  placeholder?: string | null;
  /** When true, only admin users may submit a value for this variable. The compose UI renders the input as locked / read-only for non-admins, and the server rejects non-admin send / preview requests that try to override it. Non-admins can still send the seeded `defaultValue` itself.
   */
  adminOnly?: boolean;
  /**
   * Marks the variable's initial value as coming from a persistent counter rather than the static `defaultValue`. The compose page calls `GET /templates/{id}/auto-defaults` on load and pre-fills this field with the next reference string.

   * @nullable
   */
  autoGenerate?: TemplateVariableAutoGenerate;
}

export interface Template {
  id: number;
  name: string;
  slug: string;
  category: string;
  description: string;
  layout: TemplateLayout;
  subject: string;
  /** @nullable */
  previewText?: string | null;
  bodyHtml: string;
  variables: TemplateVariable[];
  isBuiltin: boolean;
  /** @nullable */
  ownerId?: number | null;
  /** @nullable */
  sourcePrompt?: string | null;
  createdAt: string;
  /** @nullable */
  deletedAt?: string | null;
}

export interface AdminTrashedTemplate {
  template: Template;
  owner: AdminTrashedTemplateOwner;
}

export interface AdminTrashedTemplateList {
  items: AdminTrashedTemplate[];
  /** Total number of trashed templates matching the current filters (before pagination). */
  total: number;
}

/**
 * One template column in the email-volume report. `id` is null for the synthetic "(no template)" bucket.
 */
export interface EmailVolumeTemplate {
  id: number | null;
  name: string;
}

export interface EmailVolumeTemplateCount {
  templateId: number | null;
  /** @minimum 0 */
  count: number;
}

export interface EmailVolumeAgentRow {
  userId: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  email: string | null;
  /**
   * Total `sent` rows for this agent in the window (failed excluded).
   * @minimum 0
   */
  total: number;
  /**
   * Rows still `queued` in the window (counted separately as "in flight").
   * @minimum 0
   */
  queued: number;
  perTemplate: EmailVolumeTemplateCount[];
}

export interface EmailVolumeReport {
  from: string;
  to: string;
  includeTest: boolean;
  /** Stable, alphabetically-sorted list of template columns to render. `(no template)` (id=null) sorts last when present. */
  templates: EmailVolumeTemplate[];
  rows: EmailVolumeAgentRow[];
}

export interface EmailVolumeDailyPoint {
  date: string;
  /** @minimum 0 */
  sent: number;
  /** @minimum 0 */
  queued: number;
}

export interface EmailVolumeAgentTemplateDetail {
  template: EmailVolumeTemplate;
  /** @minimum 0 */
  count: number;
}

export interface EmailVolumeAgentDetail {
  userId: number;
  /** @nullable */
  name: string | null;
  /** @nullable */
  email: string | null;
  from: string;
  to: string;
  includeTest: boolean;
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  queued: number;
  perTemplate: EmailVolumeAgentTemplateDetail[];
  /** One entry per UTC day in the window, oldest-first. */
  daily: EmailVolumeDailyPoint[];
}

/**
 * Pre-image (changed fields only for updates). Null for events without one.
 */
export type AuditLogEventBefore = { [key: string]: unknown } | null;

/**
 * Post-image (changed fields only for updates). Null for events without one.
 */
export type AuditLogEventAfter = { [key: string]: unknown } | null;

/**
 * One row from the workspace audit log.
 */
export interface AuditLogEvent {
  id: number;
  /** Local users.id of the actor, or null when the actor row has been deleted or the event was system-triggered. */
  actorUserId: number | null;
  /**
   * Snapshot of the actor's email captured at insert time.
   * @nullable
   */
  actorEmail: string | null;
  /** Stable machine-readable event type (e.g. `template.updated`, `email.sent`). */
  eventType: string;
  /** Domain object the event acts on (e.g. `template`, `email`). */
  targetType: string;
  /**
   * Stable identifier of the target object as a string.
   * @nullable
   */
  targetId: string | null;
  /** Human-readable one-liner suitable for the list view. */
  description: string;
  /** Pre-image (changed fields only for updates). Null for events without one. */
  before: AuditLogEventBefore;
  /** Post-image (changed fields only for updates). Null for events without one. */
  after: AuditLogEventAfter;
  createdAt: string;
}

export interface AuditLogList {
  events: AuditLogEvent[];
  totalCount: number;
}

export type CreateTemplateInputLayout =
  (typeof CreateTemplateInputLayout)[keyof typeof CreateTemplateInputLayout];

export const CreateTemplateInputLayout = {
  minimal: "minimal",
  corporate: "corporate",
  modern_saas: "modern_saas",
  financial: "financial",
  ecommerce: "ecommerce",
  alert: "alert",
} as const;

export interface CreateTemplateInput {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name: string;
  /**
   * @minLength 1
   * @maxLength 60
   */
  category: string;
  /** @maxLength 280 */
  description: string;
  layout: CreateTemplateInputLayout;
  /**
   * @minLength 1
   * @maxLength 200
   */
  subject: string;
  /**
   * @maxLength 200
   * @nullable
   */
  previewText?: string | null;
  /** @minLength 1 */
  bodyHtml: string;
  variables: TemplateVariable[];
  /** @nullable */
  sourcePrompt?: string | null;
}

export type UpdateTemplateInputLayout =
  (typeof UpdateTemplateInputLayout)[keyof typeof UpdateTemplateInputLayout];

export const UpdateTemplateInputLayout = {
  minimal: "minimal",
  corporate: "corporate",
  modern_saas: "modern_saas",
  financial: "financial",
  ecommerce: "ecommerce",
  alert: "alert",
} as const;

export interface UpdateTemplateInput {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name?: string;
  /**
   * @minLength 1
   * @maxLength 60
   */
  category?: string;
  /** @maxLength 280 */
  description?: string;
  layout?: UpdateTemplateInputLayout;
  /**
   * @minLength 1
   * @maxLength 200
   */
  subject?: string;
  /**
   * @maxLength 200
   * @nullable
   */
  previewText?: string | null;
  /** @minLength 1 */
  bodyHtml?: string;
  variables?: TemplateVariable[];
}

/**
 * A reusable HTML fragment that templates can embed via `{{> slug}}`. Built-ins are seeded by the server (Header, Footer, Unsubscribe, CTA Button) and have `isBuiltin = true` and `ownerId = null`. User-owned snippets shadow built-ins of the same slug at render time.

 */
export interface TemplateSnippet {
  id: number;
  name: string;
  slug: string;
  description: string;
  bodyHtml: string;
  variables: TemplateVariable[];
  isBuiltin: boolean;
  /** @nullable */
  ownerId: number | null;
  createdAt: string;
  updatedAt: string;
}

export type TemplateSnippetWithUsage = TemplateSnippet & {
  /** Number of the current user's active templates that reference this snippet via `{{> slug}}`. Drives the "Used by N templates" badge in the snippets list.
   */
  usedByCount: number;
};

export interface CreateSnippetInput {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name: string;
  /** @maxLength 280 */
  description?: string;
  /** @minLength 1 */
  bodyHtml: string;
  variables?: TemplateVariable[];
}

export interface UpdateSnippetInput {
  /**
   * @minLength 1
   * @maxLength 120
   */
  name?: string;
  /** @maxLength 280 */
  description?: string;
  /** @minLength 1 */
  bodyHtml?: string;
  variables?: TemplateVariable[];
}

export interface SnippetDependent {
  id: number;
  name: string;
  slug: string;
}

export interface SnippetUsage {
  slug: string;
  dependents: SnippetDependent[];
}

export interface SnippetDeleteConflict {
  error: string;
  slug: string;
  dependents: SnippetDependent[];
}

export interface BulkDeleteTemplatesInput {
  /**
   * @minItems 1
   * @maxItems 200
   */
  ids: number[];
}

export type BulkDeleteTemplatesResultResultsItemOutcome =
  (typeof BulkDeleteTemplatesResultResultsItemOutcome)[keyof typeof BulkDeleteTemplatesResultResultsItemOutcome];

export const BulkDeleteTemplatesResultResultsItemOutcome = {
  deleted: "deleted",
  hidden: "hidden",
  not_found: "not_found",
  forbidden: "forbidden",
} as const;

export type BulkDeleteTemplatesResultResultsItem = {
  id: number;
  outcome: BulkDeleteTemplatesResultResultsItemOutcome;
};

export type BulkDeleteTemplatesResultCounts = {
  /** @minimum 0 */
  deleted: number;
  /** @minimum 0 */
  hidden: number;
  /** @minimum 0 */
  not_found: number;
  /** @minimum 0 */
  forbidden: number;
};

export interface BulkDeleteTemplatesResult {
  results: BulkDeleteTemplatesResultResultsItem[];
  counts: BulkDeleteTemplatesResultCounts;
}

export type RenderTemplateInputVariables = { [key: string]: string };

export interface RenderTemplateInput {
  variables: RenderTemplateInputVariables;
  /** @nullable */
  subjectOverride?: string | null;
}

export interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
  /** @nullable */
  previewText?: string | null;
  missingVariables: string[];
}

/**
 * Stable id of one of the supported email-client preview profiles.
 */
export type ClientProfileId =
  (typeof ClientProfileId)[keyof typeof ClientProfileId];

export const ClientProfileId = {
  "gmail-web": "gmail-web",
  "gmail-mobile": "gmail-mobile",
  "apple-mail": "apple-mail",
  "outlook-desktop": "outlook-desktop",
  "outlook-web": "outlook-web",
} as const;

/**
 * One of the email-client profiles the multi-client preview UI can switch between. The list is static and shared across the workspace.
 */
export interface ClientPreviewProfile {
  id: ClientProfileId;
  /** Human-readable tab label (e.g. "Gmail Web"). */
  label: string;
  /**
   * Width in CSS pixels the preview iframe should be sized to so the layout matches the typical reading-pane width for that client.
   * @minimum 200
   */
  viewportWidth: number;
  /** One-sentence description of what this profile approximates. */
  description: string;
  /** Short bullet-list of the well-known quirks the preview simulates (e.g. "Strips border-radius"). */
  quirks: string[];
}

/**
 * How serious this issue is. `info` is FYI, `warning` means double-check the preview, `error` means the layout will visibly break in this client.
 */
export type ClientPreviewIssueSeverity =
  (typeof ClientPreviewIssueSeverity)[keyof typeof ClientPreviewIssueSeverity];

export const ClientPreviewIssueSeverity = {
  info: "info",
  warning: "warning",
  error: "error",
} as const;

/**
 * One compatibility issue detected by the post-processor when adapting the rendered HTML to the requested client profile.
 */
export interface ClientPreviewIssue {
  /** How serious this issue is. `info` is FYI, `warning` means double-check the preview, `error` means the layout will visibly break in this client. */
  severity: ClientPreviewIssueSeverity;
  /** Stable rule id (e.g. "outlook-no-border-radius") so the UI can group / dedupe / link to docs. */
  rule: string;
  /** Single-sentence, human-readable description of the issue. */
  message: string;
  /**
   * Number of distinct elements / declarations the rule matched in this template.
   * @minimum 0
   */
  count: number;
}

export type PreviewTemplateForClientInputVariables = { [key: string]: string };

/**
 * Input for `POST /templates/{id}/preview-client`. Mirrors `RenderTemplateInput` plus the client profile to post-process the rendered HTML for.
 */
export interface PreviewTemplateForClientInput {
  variables: PreviewTemplateForClientInputVariables;
  /** @nullable */
  subjectOverride?: string | null;
  clientProfile: ClientProfileId;
}

/**
 * Result of rendering a template through one client profile's quirks pipeline.
 */
export interface RenderedClientPreview {
  subject: string;
  /** Post-processed HTML the iframe should display for this client. */
  html: string;
  text: string;
  /** @nullable */
  previewText?: string | null;
  missingVariables: string[];
  clientProfile: ClientProfileId;
  /**
   * Convenience copy of the profile's `viewportWidth` so the FE can size its iframe without a second lookup.
   * @minimum 200
   */
  viewportWidth: number;
  compatibilityIssues: ClientPreviewIssue[];
}

/**
 * The exact sender header recipients see in their inbox. Computed from the verified Resend sender address (MAILCRAFT_FROM_EMAIL) and the user's Brand "From name", with the same sanitization the server applies when sending.
 */
export interface SenderAddress {
  /** The verified Resend sender email address. */
  fromEmail: string;
  /** The full RFC 5322-style "Name <email>" string used as the From header. */
  fromAddress: string;
}

/**
 * DNS record type to select in Cloudflare's "Add record" form.
 */
export type SenderDnsRecordType =
  (typeof SenderDnsRecordType)[keyof typeof SenderDnsRecordType];

export const SenderDnsRecordType = {
  TXT: "TXT",
  CNAME: "CNAME",
  MX: "MX",
} as const;

/**
 * `verified` if Resend reports this exact record is in place, `pending` if Resend reports any other status (not detected yet), `unknown` for synthesized records (DMARC) where Resend has no opinion.

 */
export type SenderDnsRecordVerified =
  (typeof SenderDnsRecordVerified)[keyof typeof SenderDnsRecordVerified];

export const SenderDnsRecordVerified = {
  verified: "verified",
  pending: "pending",
  unknown: "unknown",
} as const;

/**
 * Categorisation used to label/group the record in the UI.
 */
export type SenderDnsRecordPurpose =
  (typeof SenderDnsRecordPurpose)[keyof typeof SenderDnsRecordPurpose];

export const SenderDnsRecordPurpose = {
  SPF: "SPF",
  DKIM: "DKIM",
  DMARC: "DMARC",
  MX: "MX",
} as const;

/**
 * One DNS record the user must add at their DNS provider (rendered as a single row in the Brand Settings copy-paste panel).
 */
export interface SenderDnsRecord {
  /** DNS record type to select in Cloudflare's "Add record" form. */
  type: SenderDnsRecordType;
  /** Fully qualified host name to enter (already domain-prefixed, e.g. `send.example.com`). */
  name: string;
  /** Value users paste into Cloudflare's "Content" / "Target" field. */
  value: string;
  /** Recommended TTL string ("Auto" matches Cloudflare's default). */
  ttl: string;
  /** `verified` if Resend reports this exact record is in place, `pending` if Resend reports any other status (not detected yet), `unknown` for synthesized records (DMARC) where Resend has no opinion.
   */
  verified: SenderDnsRecordVerified;
  /** Cloudflare-specific tip rendered next to the row. */
  note: string;
  /** Categorisation used to label/group the record in the UI. */
  purpose: SenderDnsRecordPurpose;
  /**
   * MX priority — only set when `type` is `MX`.
   * @nullable
   */
  priority: number | null;
}

/**
 * Display-only payload for the Brand Settings "DNS records (Cloudflare)" panel. Records come from Resend's verified-domain detail API; DMARC is synthesized server-side because Resend doesn't auto-create one.

 */
export interface SenderDnsRecords {
  /**
   * The verified sending domain these records belong to (null when not configured).
   * @nullable
   */
  domain: string | null;
  /** True iff a Resend domain matched the configured sender and we were able to load its DNS record bundle. The matched domain may still be pending verification — per-record `verified` status reflects that. */
  configured: boolean;
  /**
   * One-line explanation of why `records` is empty (null when records were returned).
   * @nullable
   */
  reason: string | null;
  records: SenderDnsRecord[];
  /** Server-side timestamp at which this DNS records snapshot was assembled (i.e. when Resend was last queried). Drives the "Last checked" line on the Brand Settings DNS records card so users can tell whether the per-record verification badges are fresh after they paste records into Cloudflare.
   */
  fetchedAt: string;
}

/**
 * DMARC policy advertised by the synthesized DMARC TXT record on the DNS Records card. `none` (the default) tells receivers to monitor and report on alignment failures only; `quarantine` asks them to drop unaligned mail to spam; `reject` tells them to refuse it outright. Mature senders progress none → quarantine → reject as their reports stabilise.

 */
export type BrandSettingsDmarcPolicy =
  (typeof BrandSettingsDmarcPolicy)[keyof typeof BrandSettingsDmarcPolicy];

export const BrandSettingsDmarcPolicy = {
  none: "none",
  quarantine: "quarantine",
  reject: "reject",
} as const;

export interface BrandSettings {
  id: number;
  companyName: string;
  /** @nullable */
  fromName?: string | null;
  /**
   * Optional local-part of the From email address (the bit before "@"). When set, sends use "<local-part>@<verified-shared-domain>" instead of the default verified shared sender. Lowercase, 1-64 chars, only `[a-z0-9._-]`, no leading/trailing separators.

   * @nullable
   */
  fromLocalPart?: string | null;
  /** The verified shared sending domain that all From addresses are pinned to (read-only — derived from the verified Resend sender). Useful so the UI can render the "@<domain>" suffix next to the local-part input.
   */
  fromDomain: string;
  /** @nullable */
  logoUrl?: string | null;
  primaryColor: string;
  accentColor: string;
  /** @nullable */
  websiteUrl?: string | null;
  /** @nullable */
  supportEmail?: string | null;
  /** @nullable */
  addressLine?: string | null;
  /** @nullable */
  footerNote?: string | null;
  /** @nullable */
  unsubscribeUrl?: string | null;
  /** When true, outbound (non-test) sends from this workspace have a 1x1 transparent tracking pixel injected into the HTML body. Defaults to false — open tracking is strictly opt-in per workspace.
   */
  trackOpens: boolean;
  /** When true, outbound (non-test) sends from this workspace have every `<a href="http(s)://...">` rewritten through a signed click-redirector so each click is recorded against the originating email log. Defaults to false — click tracking is strictly opt-in per workspace.
   */
  trackClicks: boolean;
  /** DMARC policy advertised by the synthesized DMARC TXT record on the DNS Records card. `none` (the default) tells receivers to monitor and report on alignment failures only; `quarantine` asks them to drop unaligned mail to spam; `reject` tells them to refuse it outright. Mature senders progress none → quarantine → reject as their reports stabilise.
   */
  dmarcPolicy: BrandSettingsDmarcPolicy;
  /**
   * Optional address that receives DMARC aggregate (XML) reports. When set, the synthesized DMARC TXT record includes a `rua=mailto:<address>` tag. Null/empty means no `rua=` tag is emitted — DMARC still works, you just won't get reports.

   * @nullable
   */
  dmarcReportingEmail?: string | null;
  updatedAt: string;
  /** When true, the brand is force-imposed by the workspace's source admin and read-only for this user. The UI should disable editing and show a "managed by" notice.
   */
  isManaged: boolean;
  /**
   * Email of the source admin who manages the workspace brand, when the brand is managed and that admin is known. Null when the requester is themselves the source admin (or in the bootstrap state where no admin has signed in yet).

   * @nullable
   */
  managedByEmail: string | null;
}

/**
 * DMARC policy for the synthesized TXT record. When omitted, the saved value is left untouched.

 */
export type UpdateBrandSettingsInputDmarcPolicy =
  (typeof UpdateBrandSettingsInputDmarcPolicy)[keyof typeof UpdateBrandSettingsInputDmarcPolicy];

export const UpdateBrandSettingsInputDmarcPolicy = {
  none: "none",
  quarantine: "quarantine",
  reject: "reject",
} as const;

export interface UpdateBrandSettingsInput {
  /**
   * @minLength 1
   * @maxLength 120
   */
  companyName: string;
  /**
   * @maxLength 120
   * @nullable
   */
  fromName?: string | null;
  /**
   * Optional local-part for the From address. Lowercase letters, digits, dot, underscore, hyphen. Must start and end with an alphanumeric character. Cannot be changed across domains — the server always pins the domain to the verified shared sender.

   * @minLength 1
   * @maxLength 64
   * @nullable
   * @pattern ^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$
   */
  fromLocalPart?: string | null;
  /** @nullable */
  logoUrl?: string | null;
  /**
   * @minLength 4
   * @maxLength 9
   */
  primaryColor: string;
  /**
   * @minLength 4
   * @maxLength 9
   */
  accentColor: string;
  /** @nullable */
  websiteUrl?: string | null;
  /** @nullable */
  supportEmail?: string | null;
  /** @nullable */
  addressLine?: string | null;
  /** @nullable */
  footerNote?: string | null;
  /** @nullable */
  unsubscribeUrl?: string | null;
  /** Toggle the workspace open-tracking pixel on or off. When omitted, the saved value is left untouched.
   */
  trackOpens?: boolean;
  /** Toggle the workspace click-tracking redirector on or off. When omitted, the saved value is left untouched.
   */
  trackClicks?: boolean;
  /** DMARC policy for the synthesized TXT record. When omitted, the saved value is left untouched.
   */
  dmarcPolicy?: UpdateBrandSettingsInputDmarcPolicy;
  /**
   * Optional `rua=mailto:` aggregate-report address. Pass `null` or an empty string to clear it. When omitted, the saved value is left untouched.

   * @maxLength 254
   * @nullable
   */
  dmarcReportingEmail?: string | null;
}

/**
 * A single workspace-defined trusted-sender domain. The UI reads these alongside the curated baseline to decide whether to show the small blue "Verified sender" tick next to a sender.

 */
export interface TrustedSender {
  id: number;
  /** Lower-cased registrable domain (e.g. `freight-co.com`). The badge matches this domain exactly or any subdomain of it, same as the curated baseline.
   */
  domain: string;
  /**
   * Email of the admin who added this entry, when known. May be null if that user has since been deleted.

   * @nullable
   */
  addedByEmail?: string | null;
  createdAt: string;
}

export interface TrustedSenderList {
  items: TrustedSender[];
}

export interface CreateTrustedSenderInput {
  /**
   * Domain to add to the workspace's trusted-sender list. Will be trimmed and lower-cased server-side. Must look like a registrable hostname — labels of `[a-z0-9-]` (no leading or trailing hyphen) separated by dots, with at least two labels. Already-on-baseline / duplicate domains are rejected with a 409.

   * @minLength 3
   * @maxLength 253
   */
  domain: string;
}

/**
 * @nullable
 */
export type AiGenerateInputLayoutHint =
  | (typeof AiGenerateInputLayoutHint)[keyof typeof AiGenerateInputLayoutHint]
  | null;

export const AiGenerateInputLayoutHint = {
  minimal: "minimal",
  corporate: "corporate",
  modern_saas: "modern_saas",
  financial: "financial",
  ecommerce: "ecommerce",
  alert: "alert",
} as const;

/**
 * @nullable
 */
export type AiGenerateInputTone =
  | (typeof AiGenerateInputTone)[keyof typeof AiGenerateInputTone]
  | null;

export const AiGenerateInputTone = {
  professional: "professional",
  friendly: "friendly",
  urgent: "urgent",
  celebratory: "celebratory",
  neutral: "neutral",
} as const;

export interface AiGenerateInput {
  /**
   * @minLength 4
   * @maxLength 1000
   */
  prompt: string;
  /**
   * @maxLength 60
   * @nullable
   */
  category?: string | null;
  /** @nullable */
  layoutHint?: AiGenerateInputLayoutHint;
  /** @nullable */
  tone?: AiGenerateInputTone;
}

export type AiGeneratedDraftLayout =
  (typeof AiGeneratedDraftLayout)[keyof typeof AiGeneratedDraftLayout];

export const AiGeneratedDraftLayout = {
  minimal: "minimal",
  corporate: "corporate",
  modern_saas: "modern_saas",
  financial: "financial",
  ecommerce: "ecommerce",
  alert: "alert",
} as const;

export interface AiGeneratedDraft {
  name: string;
  category: string;
  description: string;
  layout: AiGeneratedDraftLayout;
  subject: string;
  /** @nullable */
  previewText?: string | null;
  bodyHtml: string;
  variables: TemplateVariable[];
  sourcePrompt: string;
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

/**
 * A single RFC-5322 email address. Defined as a reusable component so every recipient field shares the same `format: email` constraint — the spec-to-zod generator picks this up and rejects malformed addresses (typos, missing `@`, trailing whitespace, etc.) before the request ever reaches Resend.

 */
export type EmailAddress = string;

export type SendEmailInputVariables = { [key: string]: unknown };

export interface EmailAttachment {
  /**
   * File name shown to the recipient (e.g. "wire-receipt.pdf").
   * @minLength 1
   * @maxLength 200
   */
  filename: string;
  /**
   * Base64-encoded file contents (no data URI prefix).
   * @minLength 1
   */
  content: string;
  /**
   * MIME type, e.g. "application/pdf".
   * @nullable
   */
  contentType?: string | null;
}

export interface SendEmailInput {
  /** @nullable */
  templateId?: number | null;
  to: EmailAddress | EmailAddress[];
  cc?: null | EmailAddress | EmailAddress[];
  bcc?: null | EmailAddress | EmailAddress[];
  /**
   * @maxLength 300
   * @nullable
   */
  subject?: string | null;
  /** @nullable */
  html?: string | null;
  /** @nullable */
  text?: string | null;
  replyToOverride?: null | EmailAddress;
  /**
   * Per-send override for the From display name. Resolution order when omitted (or null): the user's per-user Representative Defaults "From name", then the workspace Brand Settings "From name", then the built-in default display name ("Support Team"). Set on Compose when the user wants a one-off name without changing their defaults.

   * @maxLength 120
   * @nullable
   */
  fromNameOverride?: string | null;
  /**
   * Per-send override for the local-part of the From email. Resolution order when omitted (or null): the user's per-user Representative Defaults "From local part", then the workspace Brand Settings "From local part", then the verified shared sender's local-part. The domain is always pinned to the verified shared sending domain.

   * @minLength 1
   * @maxLength 64
   * @nullable
   * @pattern ^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$
   */
  fromLocalPartOverride?: string | null;
  variables?: SendEmailInputVariables;
  /**
   * Files to attach to the email. Each `content` is the file body encoded as base64 (no `data:` prefix). Total combined size of attachments must stay under ~25 MB to clear the provider limit.

   * @maxItems 10
   */
  attachments?: EmailAttachment[];
  /**
   * Optional id of an inbound email this send is a reply to. When provided, the server looks the inbound message up, sets the outbound `In-Reply-To` and `References` headers so MUAs (Gmail, Outlook, ...) thread the reply with the original on the recipient side, and persists the inbound's `thread_id` on this send's email log so the inbox detail view can interleave the reply chronologically with the rest of the thread. Takes precedence over the raw `inReplyTo` / `references` fields.

   * @nullable
   */
  inReplyToInboundEmailId?: number | null;
  /**
   * Optional RFC 5322 `In-Reply-To` header value. Set when the outbound message is a reply to another email so receiving clients can thread the conversation. Must already include the surrounding `<...>` brackets exactly as the original `Message-ID` header presented them. Ignored when `inReplyToInboundEmailId` is set (the server derives the header from the inbound message instead).

   * @maxLength 998
   * @nullable
   */
  inReplyTo?: string | null;
  /**
   * Optional RFC 5322 `References` header value. Typically the previous `References` value with the parent `Message-ID` appended (space-separated, each id wrapped in `<...>`). Used together with `inReplyTo` to thread inbox replies. Ignored when `inReplyToInboundEmailId` is set.

   * @maxLength 8000
   * @nullable
   */
  references?: string | null;
  /** Send a self-addressed preview of this draft to the authenticated user's own Clerk-managed email instead of the recipients in `to` / `cc` / `bcc`. The send still goes through the normal Resend pipeline, and its `email_logs` row is tagged `kind: "test"` so the Logs UI can surface a "Test" badge. The `to` field is still required by the schema (so callers can submit the same payload they would for a normal send) but its value is ignored when this flag is true. `cc` and `bcc` are also ignored. If the user has no Clerk email available the server returns 400.
   */
  testSend?: boolean;
}

/**
 * Lifecycle of the send. `queued` means the row was created and handed off to the QStash worker but Resend has not yet been called; the worker flips it to `sent` (with `providerMessageId`) or `failed` (with `errorMessage`) shortly after.

 */
export type EmailLogStatus =
  (typeof EmailLogStatus)[keyof typeof EmailLogStatus];

export const EmailLogStatus = {
  queued: "queued",
  sent: "sent",
  failed: "failed",
} as const;

/**
 * Classifies the send. `live` is a normal user-initiated outbound message. `test` is a self-addressed "Send test" preview that was routed to the user's own signed-up email from the Compose page. Surfaced in the Logs UI as a "Test" badge so the two are visually distinguishable. Defaults to `live` for legacy rows (the column was added later).

 */
export type EmailLogKind = (typeof EmailLogKind)[keyof typeof EmailLogKind];

export const EmailLogKind = {
  live: "live",
  test: "test",
} as const;

export interface EmailLog {
  id: number;
  fromAddress: string;
  /**
   * Display name extracted from the historical `fromAddress` header at send time (e.g. "Acme" for `Acme <hi@acme.test>`). Null when the stored header had no display name. Clients should render this directly instead of re-parsing `fromAddress`.

   * @nullable
   */
  fromName: string | null;
  /** Email address extracted from the historical `fromAddress` header at send time. Falls back to the raw stored value when the header could not be parsed.
   */
  fromEmail: string;
  /** @nullable */
  templateId?: number | null;
  /** @nullable */
  templateName?: string | null;
  to: string;
  /** @nullable */
  cc?: string | null;
  /** @nullable */
  bcc?: string | null;
  subject: string;
  /** Lifecycle of the send. `queued` means the row was created and handed off to the QStash worker but Resend has not yet been called; the worker flips it to `sent` (with `providerMessageId`) or `failed` (with `errorMessage`) shortly after.
   */
  status: EmailLogStatus;
  /** @nullable */
  providerMessageId?: string | null;
  /**
   * Friendly, user-facing explanation of why the send failed. Safe to render as the primary error text on the Logs UI. Null on success.

   * @nullable
   */
  errorMessage?: string | null;
  /**
   * Raw error text returned by the email provider (e.g. the original Resend `validation_error` body). Intended for a "Provider details" disclosure so users can triage without surfacing the technical blob next to the friendly message. Null on success, and may be null on failure when the provider gave us no extra detail.

   * @nullable
   */
  providerErrorMessage?: string | null;
  /**
   * Number of times the send-worker has actually called Resend for this row. `0` while the row is still queued and the worker has not yet picked it up. Increments to `1` on the first delivery attempt and continues to climb for each QStash redelivery triggered by a transient error (Resend 5xx / 429, network blip). A `sent` row with `attemptCount > 1` means the email eventually went out after one or more transient retries; a `failed` row's `attemptCount` is the number of attempts the send burned before giving up. Defaults to `0` for legacy rows that predate this column.

   * @minimum 0
   */
  attemptCount: number;
  sentAt: string;
  /** Classifies the send. `live` is a normal user-initiated outbound message. `test` is a self-addressed "Send test" preview that was routed to the user's own signed-up email from the Compose page. Surfaced in the Logs UI as a "Test" badge so the two are visually distinguishable. Defaults to `live` for legacy rows (the column was added later).
   */
  kind: EmailLogKind;
}

export type EmailLogEngagementClickedUrlsItem = {
  url: string;
  /** @minimum 1 */
  clicks: number;
  /** @minimum 1 */
  uniqueClickers: number;
};

/**
 * Aggregated engagement counters for one email log row. Counts exclude rows flagged `is_likely_bot` (Apple MPP, GoogleImageProxy, SafeLinks, etc.) so the values reflect human interaction rather than automated prefetches. Always present on log detail responses, even for sends that predate engagement tracking — in that case every counter is zero and the lists are empty.

 */
export interface EmailLogEngagement {
  /** @minimum 0 */
  opens: number;
  /** @minimum 0 */
  clicks: number;
  firstOpenAt: null | string;
  firstClickAt: null | string;
  /** Per-URL click breakdown for human clicks, sorted by total clicks descending. `clicks` is the raw count of click events on the URL; `uniqueClickers` approximates the number of distinct people who clicked it, derived from the IP + user-agent fingerprint of the click events (no recipient identifier is recorded by the tracking pipeline). Useful for the log detail UI to call out which CTA in the message resonated.
   */
  clickedUrls: EmailLogEngagementClickedUrlsItem[];
}

/**
 * Snapshot of the rendered template variables (key/value map) captured at send time. Null on logs that predate this snapshot column and on legacy Blank-Email sends.

 */
export type EmailLogDetailVariables = { [key: string]: string } | null;

export type InboundThreadMessageKind =
  (typeof InboundThreadMessageKind)[keyof typeof InboundThreadMessageKind];

export const InboundThreadMessageKind = {
  inbound: "inbound",
  outbound: "outbound",
} as const;

/**
 * Cryptographic verification status for the inbound message, computed at ingest from the receiving MTA's `Authentication-Results` header (DMARC alignment) plus an optional best-effort BIMI DNS lookup. `bimi` = DMARC pass + the From-domain publishes a BIMI record. `dmarc` = DMARC pass and the result row's `header.from` aligns with the displayed From-domain. `unverified` = no DMARC result was attached. `fail` = DMARC explicitly failed or the alignment domain didn't match. `null` only appears on legacy rows ingested before this column existed.

 * @nullable
 */
export type InboundVerificationStatus =
  | (typeof InboundVerificationStatus)[keyof typeof InboundVerificationStatus]
  | null;

export const InboundVerificationStatus = {
  bimi: "bimi",
  dmarc: "dmarc",
  unverified: "unverified",
  fail: "fail",
} as const;

/**
 * Per-protocol authentication check results extracted from the Authentication-Results header. Only present when verificationStatus is `fail`; null otherwise.

 * @nullable
 */
export type VerificationDetails = {
  /** SPF check result. */
  spf: string;
  /** DKIM check result. */
  dkim: string;
  /** DMARC check result. */
  dmarc: string;
} | null;

export type InboundThreadMessageAttachmentsItem = { [key: string]: unknown };

/**
 * One message in a thread, used to render the inbox detail page chronologically. Inbound messages carry their full body and attachments; outbound replies (kind="outbound") carry the body the user sent so they can be shown in the same flow.

 */
export interface InboundThreadMessage {
  kind: InboundThreadMessageKind;
  id: number;
  fromAddress: string;
  /** @nullable */
  fromName?: string | null;
  toAddress: string;
  subject: string;
  /** @nullable */
  textBody?: string | null;
  /** @nullable */
  htmlBody?: string | null;
  attachments: InboundThreadMessageAttachmentsItem[];
  /** @nullable */
  providerMessageId?: string | null;
  verificationStatus: InboundVerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  sentAt: string;
  /** @nullable */
  readAt: string | null;
}

/**
 * The full conversation this log row belongs to — inbound + outbound messages chronologically, oldest first. Empty `messages` when the log isn't part of a thread (i.e. `threadId` is NULL). Outbound messages are scoped to the requesting user; inbound messages are visible to anyone who can read the inbox. Mirrors the shape returned by `/inbox/emails/:id`.

 */
export type EmailLogDetailThread = {
  messages: InboundThreadMessage[];
};

export type EmailLogDetail = EmailLog & {
  html: string;
  /** @nullable */
  text?: string | null;
  engagement: EmailLogEngagement;
  /** Snapshot of the rendered template variables (key/value map) captured at send time. Null on logs that predate this snapshot column and on legacy Blank-Email sends.
   */
  variables?: EmailLogDetailVariables;
  /**
   * Conversation key shared with the inbound message this send is replying to (or NULL when the send was not part of a thread). Reuses the same `email_logs.thread_id` column /send populates.

   * @nullable
   */
  threadId: string | null;
  /** The full conversation this log row belongs to — inbound + outbound messages chronologically, oldest first. Empty `messages` when the log isn't part of a thread (i.e. `threadId` is NULL). Outbound messages are scoped to the requesting user; inbound messages are visible to anyone who can read the inbox. Mirrors the shape returned by `/inbox/emails/:id`.
   */
  thread: EmailLogDetailThread;
};

export type EngagementEventType =
  (typeof EngagementEventType)[keyof typeof EngagementEventType];

export const EngagementEventType = {
  open: "open",
  click: "click",
} as const;

/**
 * One recorded engagement event (open or click) for an outbound MailCraft email belonging to the signed-in user.

 */
export interface EngagementEvent {
  id: number;
  /** The `email_logs.id` this event belongs to. The Engagement page links the row's subject to `/app/logs/{emailLogId}` so users can jump from "this person opened it" back to the sent-email detail view.
   */
  emailLogId: number;
  type: EngagementEventType;
  occurredAt: string;
  /**
   * Raw client IP recorded for this event. Null only when Express couldn't determine one (e.g. direct loopback hit without an X-Forwarded-For header).

   * @nullable
   */
  ip: string | null;
  /**
   * ISO-3166 alpha-2 country code (e.g. "US"). Null when the IP couldn't be resolved (private / loopback / unknown).

   * @nullable
   */
  country: string | null;
  /**
   * City name resolved from the IP. Null when the IP only resolved to a country, or didn't resolve at all.

   * @nullable
   */
  city: string | null;
  /**
   * Original (pre-rewrite) destination URL for `type=click` rows. Always null for opens.

   * @nullable
   */
  url: string | null;
  /** True when the requesting client matched a known bot/prefetcher signature (Apple MPP, GoogleImageProxy, SafeLinks, …). Surfaced so the UI can render a small "Bot" badge on the row even when bots aren't filtered out of the list.
   */
  isLikelyBot: boolean;
  /** Recipient address from the underlying `email_logs` row. */
  to: string;
  /** Subject from the underlying `email_logs` row. */
  subject: string;
}

/**
 * Paginated response for the Engagement page. `events` is the current page, ordered newest first. `nextCursor` is the ISO-8601 timestamp to pass back as `cursor` to fetch the next page; `null` when this is the last page. `lookbackDays` echoes the server-side window so the UI can label the empty state ("No engagement events in the last X days").

 */
export interface EngagementEventList {
  events: EngagementEvent[];
  /** @nullable */
  nextCursor: string | null;
  /** @minimum 1 */
  lookbackDays: number;
}

export type SendEmailResponseWarningsItemField =
  (typeof SendEmailResponseWarningsItemField)[keyof typeof SendEmailResponseWarningsItemField];

export const SendEmailResponseWarningsItemField = {
  to: "to",
  cc: "cc",
  bcc: "bcc",
} as const;

export type SendEmailResponseWarningsItem = {
  field: SendEmailResponseWarningsItemField;
  email: string;
  verdict: EmailValidationVerdict;
};

export type SendEmailResponse = EmailLog & {
  /** Non-blocking advisory warnings collected during the send. Currently used to surface `risky` Abstract API verdicts on individual recipients (e.g. role addresses, unknown deliverability) so the Compose UI can render an inline banner. Always present; empty when there's nothing to warn about.
   */
  warnings: SendEmailResponseWarningsItem[];
};

/**
 * Input for `POST /ai/subjects`. The body's HTML is stripped down to plain text before being shown to the model so suggestions follow the actual visible content. `currentSubject` is included as context (e.g. so the model can avoid duplicating it). `variables` is an optional list of variable keys present in the draft so the model can reuse them in suggestions.

 */
export interface AiSubjectsInput {
  /**
   * @minLength 1
   * @maxLength 50000
   */
  bodyHtml: string;
  /**
   * @maxLength 300
   * @nullable
   */
  currentSubject?: string | null;
  variables?: string[];
}

export interface AiSubjectsResponse {
  subjects: string[];
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

export type AiToneInputTone =
  (typeof AiToneInputTone)[keyof typeof AiToneInputTone];

export const AiToneInputTone = {
  friendly: "friendly",
  formal: "formal",
  concise: "concise",
  apologetic: "apologetic",
  enthusiastic: "enthusiastic",
} as const;

/**
 * Input for `POST /ai/tone`. The model rewrites the draft body in the requested tone, preserving any `{{variable}}` placeholders.

 */
export interface AiToneInput {
  /**
   * @minLength 1
   * @maxLength 50000
   */
  bodyHtml: string;
  tone: AiToneInputTone;
}

export interface AiToneResponse {
  bodyHtml: string;
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

export interface AiSpamCheckIssue {
  /** @maxLength 80 */
  type: string;
  /** @maxLength 500 */
  snippet: string;
  /** @maxLength 500 */
  suggestion: string;
}

/**
 * Input for `POST /ai/spam-check`. The model returns a structured risk score plus concrete issues (spam-trigger words, ALL CAPS, excessive punctuation, link/image imbalance, missing plain-text cues, etc.) — never free-form prose.

 */
export interface AiSpamCheckInput {
  /** @maxLength 300 */
  subject: string;
  /**
   * @minLength 1
   * @maxLength 50000
   */
  bodyHtml: string;
}

export type AiSpamReportScore =
  (typeof AiSpamReportScore)[keyof typeof AiSpamReportScore];

export const AiSpamReportScore = {
  low: "low",
  medium: "medium",
  high: "high",
} as const;

export interface AiSpamReport {
  score: AiSpamReportScore;
  issues: AiSpamCheckIssue[];
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

/**
 * Input for `POST /ai/follow-up`. Loads the original sent email by id and asks the model to draft a follow-up referencing it.

 */
export interface AiFollowUpInput {
  /** @minimum 1 */
  emailLogId: number;
}

export type AiFollowUpResponseVariables = { [key: string]: string };

/**
 * Drafted follow-up. `to` carries the original recipient so Compose can pre-fill it. `variables` is the snapshot of template variables that were rendered into the original send (so the follow-up Compose form pre-fills with the same names, order ids, etc.). For legacy logs that predate that snapshot it falls back to the current template's variable defaults, and is empty when the original send was Blank or its template has been deleted.

 */
export interface AiFollowUpResponse {
  subject: string;
  bodyHtml: string;
  to: string;
  variables: AiFollowUpResponseVariables;
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

/**
 * Body returned by `POST /ai/generate-template` when the per-user AI rate limit is exhausted. Carries the current AI-rate-limit window so the client can update its banner immediately on a 429, without waiting for the next dashboard refresh.

 */
export interface AiRateLimitError {
  error: string;
  remaining: number;
  max: number;
  resetAt: string;
}

/**
 * Body returned by `POST /healthz/sender/refresh` when the per-user recheck rate limit is exhausted. The button is throttled so a user mashing it cannot hammer Resend's domains API.

 */
export interface SenderRefreshRateLimitError {
  error: string;
  remaining: number;
  max: number;
  resetAt: string;
}

/**
 * JWT vs shared-token authentication event counts.
 */
export interface PubsubAuthCounts {
  jwt: number;
  shared_token: number;
}

/**
 * Per-day JWT vs shared-token counts for a single UTC date.
 */
export interface DailyPubsubAuthBucket {
  date: string;
  jwt: number;
  shared_token: number;
}

/**
 * Pub/Sub authentication metrics broken down by in-process counters, historical (flushed-to-DB) totals, a combined total, and a 30-day daily time series.

 */
export interface PubsubMetricsResponse {
  process: PubsubAuthCounts;
  historical: PubsubAuthCounts;
  total: PubsubAuthCounts;
  daily: DailyPubsubAuthBucket[];
}

/**
 * Send activity for a single UTC day used to render the dashboard 14-day activity chart.

 */
export interface DashboardDailyVolume {
  /** UTC date in `YYYY-MM-DD` form. */
  date: string;
  /** @minimum 0 */
  sent: number;
  /** @minimum 0 */
  failed: number;
}

export interface DashboardTemplateUsage {
  templateId: number;
  templateName: string;
  sentCount: number;
}

/**
 * One UTC day of engagement counters for the dashboard trend chart. `sent` counts `live` sends only (test sends are excluded so the rate trend isn't biased by self-addressed previews).

 */
export interface DashboardDailyEngagement {
  /** UTC date in `YYYY-MM-DD` form. */
  date: string;
  /** @minimum 0 */
  sent: number;
  /** @minimum 0 */
  opens: number;
  /** @minimum 0 */
  clicks: number;
}

/**
 * Open + click engagement aggregates for the user's last 14 UTC days of `live` sends. Counts ignore `is_likely_bot` rows so the rates approximate human engagement. `dailyEngagement` is the per-day series the dashboard uses to draw the trend chart; days with no activity have all-zero counters so the array always carries 14 entries oldest first.

 */
export interface DashboardEngagement {
  /** @minimum 0 */
  sentLast14Days: number;
  /** @minimum 0 */
  opensLast14Days: number;
  /** @minimum 0 */
  clicksLast14Days: number;
  /**
   * Distinct logs (one recipient = one log) that recorded at least one human open in the window. Used as the numerator for `openRate14Days` so resends to the same address don't distort the percentage when one user opens twice.

   * @minimum 0
   */
  uniqueOpensLast14Days: number;
  /**
   * Distinct logs with at least one human click.
   * @minimum 0
   */
  uniqueClicksLast14Days: number;
  /**
   * `uniqueOpensLast14Days / sentLast14Days`, clamped to [0, 1]. Returns 0 when there were no sends in the window so the UI can render "—" without dividing by zero.

   * @minimum 0
   * @maximum 1
   */
  openRate14Days: number;
  /**
   * @minimum 0
   * @maximum 1
   */
  clickRate14Days: number;
  dailyEngagement: DashboardDailyEngagement[];
}

export interface DashboardSummary {
  totalSent: number;
  totalFailed: number;
  sentLast7Days: number;
  failedLast7Days: number;
  uniqueRecipientsTotal: number;
  uniqueRecipientsLast7Days: number;
  userTemplatesCount: number;
  builtinTemplatesCount: number;
  aiRateLimitRemaining: number;
  aiRateLimitMax: number;
  aiRateLimitResetAt: string;
  senderHealth: SenderConfig;
  /** Per-day send and failure counts for the last 14 calendar days (UTC), oldest first. Days with no activity are returned as zeros so the array always has 14 entries.
   */
  dailyVolume: DashboardDailyVolume[];
  recentLogs: EmailLog[];
  topTemplates: DashboardTemplateUsage[];
  engagement: DashboardEngagement;
}

/**
 * One UTC day of engagement counters for the per-template trend chart. `sent` counts `live` sends only (test sends are excluded so the rate trend isn't biased by self-addressed previews).

 */
export interface TemplateEngagementDaily {
  /** UTC date in `YYYY-MM-DD` form. */
  date: string;
  /** @minimum 0 */
  sent: number;
  /** @minimum 0 */
  opens: number;
  /** @minimum 0 */
  clicks: number;
}

/**
 * Per-template engagement aggregates for the signed-in user's own `live` sends. Lifetime fields cover all sends ever made of this template by this user; the 30-day window mirrors the dashboard's bot-filtered, unique-per-log calculation but scoped to a single template id. `daily30` is zero-filled so the trend chart always carries 30 oldest-first entries even on quiet templates.

 */
export interface TemplateEngagement {
  templateId: number;
  /**
   * Total `live` sends with `status = 'sent'` ever made of this template by the current user.
   * @minimum 0
   */
  lifetimeSends: number;
  /**
   * Total non-bot open events recorded against the user's lifetime sends of this template.
   * @minimum 0
   */
  lifetimeOpens: number;
  /**
   * Total non-bot click events recorded against the user's lifetime sends of this template.
   * @minimum 0
   */
  lifetimeClicks: number;
  /**
   * Distinct sends (one log = one recipient) that recorded at least one human open. Numerator for `lifetimeOpenRate` so resends to the same address don't push the rate past 100%.
   * @minimum 0
   */
  uniqueLifetimeOpens: number;
  /**
   * Distinct sends that recorded at least one human click.
   * @minimum 0
   */
  uniqueLifetimeClicks: number;
  /**
   * `uniqueLifetimeOpens / lifetimeSends`, clamped to [0, 1]. Returns 0 when the template has no sends so the UI can render "—" without dividing by zero.

   * @minimum 0
   * @maximum 1
   */
  lifetimeOpenRate: number;
  /**
   * @minimum 0
   * @maximum 1
   */
  lifetimeClickRate: number;
  /** @minimum 0 */
  sent30Days: number;
  /** @minimum 0 */
  opens30Days: number;
  /** @minimum 0 */
  clicks30Days: number;
  /** @minimum 0 */
  uniqueOpens30Days: number;
  /** @minimum 0 */
  uniqueClicks30Days: number;
  /**
   * @minimum 0
   * @maximum 1
   */
  openRate30Days: number;
  /**
   * @minimum 0
   * @maximum 1
   */
  clickRate30Days: number;
  /** Per-UTC-day series for the 30-day trend chart, oldest first. */
  daily30: TemplateEngagementDaily[];
}

export type ContactValidationErrorCode =
  (typeof ContactValidationErrorCode)[keyof typeof ContactValidationErrorCode];

export const ContactValidationErrorCode = {
  email_invalid: "email_invalid",
} as const;

/**
 * 400 body returned by `POST /contacts` when the supplied email is classified as `invalid` by Abstract API and the caller did not opt into `allowInvalid: true`. The UI uses `verdict.reasons` to explain why the address was rejected.

 */
export interface ContactValidationError {
  error: string;
  code: ContactValidationErrorCode;
  verdict: EmailValidationVerdict;
}

export type SendValidationErrorCode =
  (typeof SendValidationErrorCode)[keyof typeof SendValidationErrorCode];

export const SendValidationErrorCode = {
  recipient_invalid: "recipient_invalid",
} as const;

export type SendValidationErrorInvalidItemField =
  (typeof SendValidationErrorInvalidItemField)[keyof typeof SendValidationErrorInvalidItemField];

export const SendValidationErrorInvalidItemField = {
  to: "to",
  cc: "cc",
  bcc: "bcc",
} as const;

export type SendValidationErrorInvalidItem = {
  field: SendValidationErrorInvalidItemField;
  email: string;
  verdict: EmailValidationVerdict;
};

/**
 * 400 body returned by `POST /send` when one or more recipient addresses (To/Cc/Bcc) are classified as `invalid` by Abstract API. The `invalid` array carries one entry per failing address with its verdict so the Compose UI can surface a per-address explanation. `risky` recipients do not block — the send proceeds and risky verdicts are reported via `SendEmailResponse.warnings` instead.

 */
export interface SendValidationError {
  error: string;
  code: SendValidationErrorCode;
  invalid: SendValidationErrorInvalidItem[];
}

export interface Contact {
  id: number;
  name: string;
  /** @nullable */
  email?: string | null;
  /** @nullable */
  phone?: string | null;
  /** @nullable */
  address?: string | null;
  /** Cached validation verdict for this contact's email, captured on create / bulk-import. Null when the contact has no email or when validation has never run.
   */
  validationStatus: null | "valid" | "risky" | "invalid";
  validationReasons: null | string[];
  validatedAt: null | string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContactInput {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /**
   * @maxLength 320
   * @nullable
   */
  email?: string | null;
  /**
   * @maxLength 50
   * @nullable
   */
  phone?: string | null;
  /**
   * @maxLength 1000
   * @nullable
   */
  address?: string | null;
  /** When `true`, persist the contact even if Abstract API classifies the email as `invalid`. Defaults to `false`, in which case invalid emails are rejected with `400`.
   */
  allowInvalid?: boolean;
}

export type CreateContactResponse = Contact & {
  verdict?: null | EmailValidationVerdict;
};

export interface UpdateContactInput {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /**
   * @maxLength 320
   * @nullable
   */
  email?: string | null;
  /**
   * @maxLength 50
   * @nullable
   */
  phone?: string | null;
  /**
   * @maxLength 1000
   * @nullable
   */
  address?: string | null;
}

export type BulkImportContactsInputRowsItem = {
  /**
   * @minLength 1
   * @maxLength 200
   */
  name: string;
  /**
   * @maxLength 320
   * @nullable
   */
  email?: string | null;
  /**
   * @maxLength 50
   * @nullable
   */
  phone?: string | null;
  /**
   * @maxLength 1000
   * @nullable
   */
  address?: string | null;
};

/**
 * Bulk-import a batch of contacts. Each row's email is validated with Abstract API (cached). Rows whose email is `invalid` are skipped by default; set `allowInvalid: true` to import them anyway. `risky` rows are imported with a warning badge.

 */
export interface BulkImportContactsInput {
  /**
   * @minItems 1
   * @maxItems 500
   */
  rows: BulkImportContactsInputRowsItem[];
  /** Persist rows whose email Abstract flags as `invalid`. Defaults to `false`.
   */
  allowInvalid?: boolean;
}

/**
 * Result of validating + (optionally) importing a single bulk-import row. `imported` is `true` when the row was saved as a contact; `false` when it was skipped because its email was `invalid` and `allowInvalid` was not set. The `verdict` carries the per-row Abstract verdict (or null when the row had no email).

 */
export interface BulkImportRowResult {
  /** @minimum 0 */
  index: number;
  name: string;
  /** @nullable */
  email: string | null;
  imported: boolean;
  contact: null | Contact;
  verdict: null | EmailValidationVerdict;
}

export type ContactImportSummarySummary = {
  /** @minimum 0 */
  total: number;
  /** @minimum 0 */
  valid: number;
  /** @minimum 0 */
  risky: number;
  /** @minimum 0 */
  invalid: number;
  /** @minimum 0 */
  imported: number;
  /** @minimum 0 */
  skipped: number;
};

export interface ContactImportSummary {
  summary: ContactImportSummarySummary;
  rows: BulkImportRowResult[];
}

export interface RepDefaults {
  /** @nullable */
  repName: string | null;
  /** @nullable */
  repPhone: string | null;
  /** @nullable */
  repEmail: string | null;
  /** @nullable */
  employeeId: string | null;
  /** @nullable */
  caseId: string | null;
  /** @nullable */
  agentTitle: string | null;
  /** @nullable */
  agentDepartment: string | null;
  /** @nullable */
  fromName: string | null;
  /** @nullable */
  fromLocalPart: string | null;
  fromDomain: string;
  /** @nullable */
  handwrittenSignatureUrl: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface NextCaseReference {
  /** @nullable */
  nextCaseReference: string | null;
}

export interface UpdateRepDefaultsInput {
  /**
   * @maxLength 200
   * @nullable
   */
  repName?: string | null;
  /**
   * @maxLength 50
   * @nullable
   */
  repPhone?: string | null;
  /**
   * @maxLength 320
   * @nullable
   */
  repEmail?: string | null;
  /**
   * @maxLength 100
   * @nullable
   */
  employeeId?: string | null;
  /**
   * @maxLength 100
   * @nullable
   */
  caseId?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  agentTitle?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  agentDepartment?: string | null;
  /**
   * @maxLength 200
   * @nullable
   */
  fromName?: string | null;
  /**
   * @minLength 1
   * @maxLength 64
   * @nullable
   * @pattern ^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$
   */
  fromLocalPart?: string | null;
}

export type InboundMailboxStatus =
  (typeof InboundMailboxStatus)[keyof typeof InboundMailboxStatus];

export const InboundMailboxStatus = {
  pending: "pending",
  active: "active",
  failed: "failed",
  disabled: "disabled",
} as const;

/**
 * @nullable
 */
export type InboundMailboxLastWebhookOutcome =
  | (typeof InboundMailboxLastWebhookOutcome)[keyof typeof InboundMailboxLastWebhookOutcome]
  | null;

export const InboundMailboxLastWebhookOutcome = {
  accepted: "accepted",
  duplicate: "duplicate",
  persist_failed: "persist_failed",
} as const;

/**
 * A configured inbound mailbox (e.g. `sales@example.com`). Status starts as `pending` until the first signed webhook delivery arrives, at which point it becomes `active`. `disabled` is the soft-deleted state — the row is preserved so historical messages stay attached, but the webhook will reject new mail addressed to it.
The `lastWebhookAttempt*` fields surface delivery diagnostics: when (or whether) the inbound webhook has ever processed a message addressed to this mailbox, the outcome of that last attempt, and a short error string when it failed. They power the Brand Settings diagnostics strip so operators can tell why a `pending` mailbox isn't yet receiving mail without grepping server logs.

 */
export interface InboundMailbox {
  id: number;
  address: string;
  /** @nullable */
  displayName?: string | null;
  status: InboundMailboxStatus;
  /** Number of distinct unread inbound threads currently in the mailbox. Counts threads, not messages, so it matches the inbox list view.
   */
  unreadCount: number;
  /**
   * Total number of inbound messages ever received on this mailbox (including read ones and disabled-mailbox history). Used by the Brand panel to show how many archived messages would re-appear if a soft-deleted mailbox is restored.

   * @minimum 0
   */
  messageCount: number;
  createdAt: string;
  /**
   * When this mailbox was soft-deleted (status flipped to `disabled`). NULL while the mailbox is active / pending / failed. The Brand Settings "Show removed mailboxes" disclosure renders this as a relative timestamp ("removed 3 days ago") so operators can decide whether a leftover mailbox is safe to permanently purge.

   * @nullable
   */
  disabledAt: string | null;
  /** @nullable */
  lastWebhookAttemptAt: string | null;
  /** @nullable */
  lastWebhookOutcome: InboundMailboxLastWebhookOutcome;
  /** @nullable */
  lastWebhookError: string | null;
}

/**
 * Body for `POST /inbox/mailboxes`. Only the local-part is supplied — the domain is fixed by the server's inbound configuration. The local-part is normalized to lowercase and must match the regex `^[A-Za-z0-9._+-]+$` (RFC 5321 dot-atom, minus quoted forms). `displayName` is optional; when omitted, the server falls back to the local-part.

 */
export interface CreateInboundMailboxInput {
  /**
   * @minLength 1
   * @maxLength 64
   * @pattern ^[A-Za-z0-9._+-]+$
   */
  localPart: string;
  /**
   * @minLength 1
   * @maxLength 120
   */
  displayName?: string;
}

/**
 * Body for `PATCH /inbox/mailboxes/{id}`. Only the display name is editable; changing the address would orphan past inbound messages and is intentionally not supported.

 */
export interface UpdateInboundMailboxInput {
  /**
   * @minLength 1
   * @maxLength 120
   */
  displayName: string;
}

/**
 * Process-level inbound webhook health snapshot. Counters and timestamps reset on each API server restart by design — they describe the running process so the operator can see whether a fix has actually settled the problem.

 */
export interface InboundWebhookDiagnostics {
  /** True when both `SENDGRID_INBOUND_BASIC_AUTH_USER` and `SENDGRID_INBOUND_BASIC_AUTH_PASSWORD` are set on the server. False if either is missing — the inbound route will return 503 in that case.
   */
  basicAuthConfigured: boolean;
  /** @nullable */
  lastSignatureFailureAt: string | null;
  /** @minimum 0 */
  signatureFailureCount: number;
  /** @nullable */
  lastUnknownRecipientAt: string | null;
  /** @nullable */
  lastUnknownRecipientAddress: string | null;
  /** @minimum 0 */
  unknownRecipientCount: number;
}

/**
 * List of all configured inbound mailboxes plus the inbound domain currently in use (so the Settings page can show the provider DNS / forwarding target). The `diagnostics` block surfaces process-level webhook health (signature secret configured? recent signature failures? recent unknown recipients?) so the Brand Settings page can warn the operator about issues that aren't attributable to a single mailbox.

 */
export interface InboundMailboxList {
  domain: string;
  mailboxes: InboundMailbox[];
  diagnostics: InboundWebhookDiagnostics;
}

/**
 * Body for `POST /inbox/test-webhook`. The server will synthesize a minimal inbound email payload addressed to the given mailbox and run it through the same persist + threading pipeline live SendGrid Inbound Parse deliveries take.

 */
export interface TestInboundWebhookInput {
  mailboxId: number;
}

/**
 * Result of `POST /inbox/test-webhook`. `ok` is true when the persist pipeline returned a 2xx-equivalent status. `status` and `body` echo the persist result so the operator can see the exact failure (e.g. "no matching mailbox" when the recipient isn't configured).

 */
export interface TestInboundWebhookResult {
  ok: boolean;
  status: number;
  body: string;
}

/**
 * Count per verification status. Keys are `bimi`, `dmarc`, `unverified`, `fail`.

 */
export type InboundVerificationStatsBreakdown = {
  bimi: number;
  dmarc: number;
  unverified: number;
  fail: number;
};

/**
 * Aggregate verification-status breakdown for inbound messages over a configurable lookback window.

 */
export interface InboundVerificationStats {
  /** Number of days the stats cover. */
  days: number;
  /** Total inbound messages in the window. */
  total: number;
  /** Count per verification status. Keys are `bimi`, `dmarc`, `unverified`, `fail`.
   */
  breakdown: InboundVerificationStatsBreakdown;
}

export type MailboxVerificationStatsBreakdown = {
  bimi: number;
  dmarc: number;
  unverified: number;
  fail: number;
};

/**
 * Verification-status breakdown for a single mailbox.

 */
export interface MailboxVerificationStats {
  mailboxId: number;
  address: string;
  displayName: string | null;
  total: number;
  breakdown: MailboxVerificationStatsBreakdown;
}

/**
 * One thread in the inbox list view. Each row represents a whole conversation, not a single message: the surfaced fields come from the latest message in the thread, and `threadCount` / `threadUnreadCount` summarize the rest. `id` is the latest message's inbound id and is what the detail page is keyed on.

 */
export interface InboundEmailListItem {
  id: number;
  /** @nullable */
  threadId: string | null;
  mailboxId: number;
  mailboxAddress: string;
  fromAddress: string;
  /** @nullable */
  fromName?: string | null;
  toAddress: string;
  subject: string;
  snippet: string;
  verificationStatus: InboundVerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  receivedAt: string;
  /** @nullable */
  readAt: string | null;
  /**
   * Number of inbound messages in this thread.
   * @minimum 1
   */
  threadCount: number;
  /**
   * Number of unread inbound messages in this thread.
   * @minimum 0
   */
  threadUnreadCount: number;
  /**
   * When the thread was pinned by the signed-in user, or null.
   * @nullable
   */
  pinnedAt: string | null;
  /**
   * When the thread was snoozed until. In `active` view this is always either null or `<= now()` (because future-snoozed threads are filtered out). When non-null in the active view the row also carries `justUnsnoozed=true`.

   * @nullable
   */
  snoozedUntil: string | null;
  /** @nullable */
  reminderAt: string | null;
  /**
   * When the per-user reminder for this thread fired (i.e. `reminderAt <= now()` and no inbound arrived after the reminder was set). Used by the UI to render the "Reminder" badge until the user dismisses it.

   * @nullable
   */
  reminderFiredAt: string | null;
  /** True when the thread's snooze just expired and the user has not yet dismissed the "Snoozed until X" badge.
   */
  justUnsnoozed: boolean;
}

export type InboundEmailDetailAttachmentsItem = { [key: string]: unknown };

export type InboundEmailDetailHeadersItem = { [key: string]: unknown };

export type InboundEmailDetailThread = {
  messages: InboundThreadMessage[];
};

/**
 * Full inbound email record for the message identified by `id`, plus the entire conversation it belongs to in `thread.messages` (chronological, oldest first, including outbound replies the user has sent via /send). HTML bodies must be rendered in a sandboxed iframe — content is untrusted.

 */
export interface InboundEmailDetail {
  id: number;
  /** @nullable */
  threadId: string | null;
  mailboxId: number;
  mailboxAddress: string;
  fromAddress: string;
  /** @nullable */
  fromName?: string | null;
  toAddress: string;
  subject: string;
  /** @nullable */
  textBody?: string | null;
  /** @nullable */
  htmlBody?: string | null;
  attachments: InboundEmailDetailAttachmentsItem[];
  headers: InboundEmailDetailHeadersItem[];
  /** @nullable */
  providerMessageId?: string | null;
  /**
   * Pre-computed `In-Reply-To` value the client should send when replying to this message — derived server-side from the inbound `Message-ID` header (falling back to the provider's `MessageID` wrapped in angle brackets). Null if no Message-ID could be determined.

   * @nullable
   */
  inReplyTo?: string | null;
  /**
   * Pre-computed `References` value the client should send when replying. Built from the inbound `References` header (or `In-Reply-To` if no References) followed by the inbound `Message-ID`, all space-separated. Null if no usable Message-ID was present.

   * @nullable
   */
  references?: string | null;
  verificationStatus: InboundVerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  receivedAt: string;
  /** @nullable */
  readAt: string | null;
  thread: InboundEmailDetailThread;
}

/**
 * Body for `PATCH /inbox/emails/{id}/read`. Toggle the `readAt` timestamp on or off.

 */
export interface SetInboundEmailReadStateInput {
  read: boolean;
}

/**
 * Body for `PUT /inbox/threads/{kind}/{threadId}/state`. Each field is independent: omitting a field leaves the column untouched, passing `null` clears it, and passing a value sets it.

 */
export interface SetThreadStateInput {
  /**
   * `true` to pin the thread (sets `pinnedAt = now`). `false` or `null` to unpin.

   * @nullable
   */
  pinned?: boolean | null;
  /**
   * ISO timestamp at which the thread should re-appear in the active inbox view. `null` cancels the snooze.

   * @nullable
   */
  snoozedUntil?: string | null;
  /**
   * ISO timestamp at which to fire a "remind me" badge on this thread, provided no inbound message arrives between the time the reminder is set and `reminderAt`. `null` cancels the reminder.

   * @nullable
   */
  reminderAt?: string | null;
}

export type ThreadUserStateThreadKind =
  (typeof ThreadUserStateThreadKind)[keyof typeof ThreadUserStateThreadKind];

export const ThreadUserStateThreadKind = {
  inbound: "inbound",
  gmail: "gmail",
} as const;

/**
 * Per-user productivity state attached to one inbox thread.

 */
export interface ThreadUserState {
  threadKind: ThreadUserStateThreadKind;
  threadId: string;
  /** @nullable */
  pinnedAt: string | null;
  /** @nullable */
  snoozedUntil: string | null;
  /** @nullable */
  reminderAt: string | null;
  /** @nullable */
  reminderSetAt: string | null;
  /** @nullable */
  reminderFiredAt: string | null;
}

/**
 * Body for `PUT /reply-drafts/{kind}/{ref}`. The client sends the full subject + body on every (debounced) save.

 */
export interface SaveReplyDraftInput {
  /**
   * Reply subject line. Capped at the same 998-char limit RFC 5322 §2.1.1 places on header lines so a runaway keystroke loop can't blow the row out.

   * @maxLength 998
   */
  subject: string;
  /**
   * Reply body in plain text. Capped at 200_000 characters — comfortably above any real reply, well below what would embarrass a Postgres row.

   * @maxLength 200000
   */
  body: string;
  /** True when the body originated from an AI draft. Drives the "AI draft" pill in the inline composer when the draft is restored.
   */
  isAiDrafted?: boolean;
}

export type ReplyDraftThreadKind =
  (typeof ReplyDraftThreadKind)[keyof typeof ReplyDraftThreadKind];

export const ReplyDraftThreadKind = {
  inbound: "inbound",
  gmail: "gmail",
} as const;

/**
 * Saved inline-reply draft. Returned from `GET` and `PUT` on `/reply-drafts/{kind}/{ref}`.

 */
export interface ReplyDraft {
  threadKind: ReplyDraftThreadKind;
  threadRef: string;
  subject: string;
  body: string;
  isAiDrafted: boolean;
  updatedAt: string;
}

/**
 * Optional tone hint. `auto` keeps the historical concise/helpful default. The other three bias the model toward warmer, more formal, or shorter prose respectively.

 */
export type AiReplyDraftInputTone =
  (typeof AiReplyDraftInputTone)[keyof typeof AiReplyDraftInputTone];

export const AiReplyDraftInputTone = {
  auto: "auto",
  friendly: "friendly",
  formal: "formal",
  concise: "concise",
} as const;

/**
 * Body for `POST /ai/reply-draft`. Identifies the source thread either by `inboundEmailId` (workspace inbound mailbox) OR `gmailMessageId` (current user's Gmail). Exactly one must be set. `tone` optionally biases the model toward a particular register; the default ("auto") reuses the historical concise/ helpful voice.

 */
export interface AiReplyDraftInput {
  /**
   * Workspace inbound email id. Mutually exclusive with `gmailMessageId` — the handler returns 400 when both (or neither) are supplied.

   * @minimum 1
   */
  inboundEmailId?: number;
  /**
   * Gmail API message id for the message the user is replying to. The endpoint will fetch the full thread (subject + all messages) so the draft can quote/reference prior turns. Mutually exclusive with `inboundEmailId`.

   * @minLength 1
   * @maxLength 200
   */
  gmailMessageId?: string;
  /** Optional tone hint. `auto` keeps the historical concise/helpful default. The other three bias the model toward warmer, more formal, or shorter prose respectively.
   */
  tone?: AiReplyDraftInputTone;
}

/**
 * AI-generated reply draft. NEVER sent automatically; the client renders these values in the inline reply composer so a human can review (and tweak) before sending. The body always ends with the rep's saved signature block — when the rep has not yet filled in their defaults, `repDefaultsMissing` is true so the UI can prompt them to do so.

 */
export interface AiReplyDraft {
  subject: string;
  body: string;
  /** The original sender (where the reply should be addressed). */
  toAddress: string;
  /** The mailbox/account the original message was sent to. For inbound replies this is the configured inbound mailbox; for Gmail replies it's the user's Gmail address.
   */
  fromMailbox: string;
  /** True when the rep has not yet saved at least their name (and ideally title + phone). Lets the UI surface a one-line "Add your name and phone for better signatures →" nudge without a second round-trip.
   */
  repDefaultsMissing: boolean;
  rateLimitRemaining: number;
  rateLimitMax: number;
  rateLimitResetAt: string;
}

/**
 * @nullable
 */
export type GmailStatusReason =
  | (typeof GmailStatusReason)[keyof typeof GmailStatusReason]
  | null;

export const GmailStatusReason = {
  not_google_user: "not_google_user",
  gmail_not_connected: "gmail_not_connected",
  gmail_scope_missing: "gmail_scope_missing",
  gmail_token_revoked: "gmail_token_revoked",
} as const;

/**
 * Connection state of the signed-in user's Gmail OAuth grant. `connected` means MailCraft holds a Google access token that carries at least the `gmail.readonly` (or `gmail.modify`) scope. `scopes` lists every Gmail scope on the active token so the UI can decide whether to expose write actions like reply, archive, or label edits — and prompt the user to reconnect if any of the new write scopes are missing. `reason` is set when `connected` is false; the UI uses it to show the right prompt:
  - `not_google_user`: signed in via email/password (or another
    non-Google provider). Hide the entire Gmail section.
  - `gmail_not_connected`: Google account is linked but no
    Gmail OAuth token is on file. Show "Connect Gmail" CTA.
  - `gmail_scope_missing`: Google account is linked but the
    granted scopes do not include gmail.readonly. Show
    "Reauthorize Gmail" CTA.
  - `gmail_token_revoked`: token exists but Gmail rejected it
    (revoked, expired, or otherwise invalid). Same CTA as
    `gmail_scope_missing`.

 */
export interface GmailStatus {
  connected: boolean;
  /**
   * Email address of the linked Google account, when one is attached. May be present even when `connected` is false (e.g. Google is linked but the gmail.readonly scope is missing) so the UI can render "Connect <address>".

   * @nullable
   */
  gmailAddress: string | null;
  /** @nullable */
  reason: GmailStatusReason;
  /** All Gmail OAuth scopes granted on the user's active token. Empty when the user is not connected. The UI compares this against the scopes required by each write action (send, modify) and surfaces an inline reconnect prompt when one is missing.
   */
  scopes: string[];
  /**
   * ISO-8601 timestamp of when the current Gmail Pub/Sub watch expires, or null if no watch row exists. Gmail watches last up to 7 days; a background job refreshes them before expiry. Surfaced here for observability so operators and power users can confirm the watch is healthy.

   * @nullable
   */
  watchExpiresAt: string | null;
}

export type GmailUnavailableErrorReason =
  (typeof GmailUnavailableErrorReason)[keyof typeof GmailUnavailableErrorReason];

export const GmailUnavailableErrorReason = {
  not_google_user: "not_google_user",
  gmail_not_connected: "gmail_not_connected",
  gmail_scope_missing: "gmail_scope_missing",
  gmail_send_scope_missing: "gmail_send_scope_missing",
  gmail_token_revoked: "gmail_token_revoked",
} as const;

/**
 * Returned by Gmail message endpoints when the signed-in user's Google OAuth grant cannot satisfy the request. Carries the same `reason` codes as `GmailStatus.reason` so the UI can render the appropriate CTA without a second round-trip. Write endpoints additionally include `missingScope` when the user is connected but their token lacks the specific scope required by that action (`gmail.send` for reply, `gmail.modify` for everything else).

 */
export interface GmailUnavailableError {
  error: string;
  reason: GmailUnavailableErrorReason;
  /** @nullable */
  gmailAddress?: string | null;
  /** The OAuth scope the user must grant to retry. Only present when `reason === "gmail_scope_missing"`.
   */
  missingScope?: string;
}

/**
 * Which Gmail inbox tab this message would show up under. Derived from the message's CATEGORY_* labelIds; defaults to `primary` for messages without an explicit category label (which is how Gmail renders them too).

 */
export type GmailMessageListItemCategory =
  (typeof GmailMessageListItemCategory)[keyof typeof GmailMessageListItemCategory];

export const GmailMessageListItemCategory = {
  primary: "primary",
  promotions: "promotions",
  social: "social",
  updates: "updates",
  forums: "forums",
} as const;

/**
 * One row in the Gmail inbox listing. Mirrors the shape of `InboundEmailListItem` so the UI can render a similar row, but uses Gmail's opaque message id (a hex string) instead of an integer. The listing now spans all five Gmail inbox categories (Primary, Promotions, Social, Updates, Forums); the per-row `category` field tells the UI which one this message belongs to so it can render a badge.

 */
export interface GmailMessageListItem {
  id: string;
  threadId: string;
  /** @nullable */
  subject: string | null;
  fromAddress: string;
  /** @nullable */
  fromName: string | null;
  /** @nullable */
  toAddress: string | null;
  snippet: string;
  receivedAt: string;
  isUnread: boolean;
  /** Which Gmail inbox tab this message would show up under. Derived from the message's CATEGORY_* labelIds; defaults to `primary` for messages without an explicit category label (which is how Gmail renders them too).
   */
  category: GmailMessageListItemCategory;
  verificationStatus: InboundVerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  /** @nullable */
  pinnedAt: string | null;
  /** @nullable */
  snoozedUntil: string | null;
  /** @nullable */
  reminderAt: string | null;
  /** @nullable */
  reminderFiredAt: string | null;
  justUnsnoozed: boolean;
}

export interface GmailMessageList {
  /**
   * Email address of the linked Google account. Null when the address could not be derived from Clerk and the `users.getProfile` fallback also returned no address. The UI should treat null and empty string as equivalent and fall back to the user's primary Clerk email when displaying.

   * @nullable
   */
  gmailAddress: string | null;
  messages: GmailMessageListItem[];
  /**
   * Total number of unread messages currently in the user's Gmail Inbox (the `messagesUnread` counter on the `INBOX` system label). Drives the unread badge in the Gmail section header on the Inbox page. `null` when Gmail's labels endpoint failed for a non-auth reason — the UI simply hides the badge in that case rather than blocking the entire listing.

   * @minimum 0
   * @nullable
   */
  unreadCount: number | null;
}

export type SearchResultItemKind =
  (typeof SearchResultItemKind)[keyof typeof SearchResultItemKind];

export const SearchResultItemKind = {
  sent: "sent",
  inbound: "inbound",
  gmail: "gmail",
} as const;

/**
 * One row in the merged search results. The `kind` discriminator tells the UI which detail page to navigate to and which Badge variant to render. `id` is a string because Gmail message ids are opaque hex tokens — sent and inbound id's are stringified integers to keep the union type uniform on the wire.

 */
export interface SearchResultItem {
  kind: SearchResultItemKind;
  id: string;
  subject: string;
  snippet: string;
  fromAddress: string;
  /** @nullable */
  fromName: string | null;
  toAddress: string;
  /** Unified time field used for ordering. Maps to `email_logs.sent_at` for sent rows, `inbound_emails.received_at` for inbound, and Gmail's `internalDate` for Gmail.
   */
  occurredAt: string;
  /**
   * Sent-only delivery status (e.g. sent / failed / queued). Null for inbound + Gmail rows.
   * @nullable
   */
  status: string | null;
  /**
   * Sent-only template id, when the send was driven by a template. Null otherwise.
   * @nullable
   */
  templateId: number | null;
  /**
   * Sent-only template name (denormalized via JOIN). Null otherwise.
   * @nullable
   */
  templateName: string | null;
  hasAttachment: boolean;
  /** @nullable */
  mailboxAddress: string | null;
  verificationStatus: InboundVerificationStatus | null;
}

/**
 * Paginated, time-ordered union of sent + inbound + Gmail rows. `total` is the count of Postgres-side matches (sent + inbound) only — Gmail's API does not return a stable count, so the UI should treat `total` as a lower bound when Gmail rows are included. `gmailIncluded` tells the UI whether the Gmail bucket was queried at all (it's skipped silently when the signed-in user has no usable Gmail OAuth grant).

 */
export interface SearchResults {
  items: SearchResultItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
  gmailIncluded: boolean;
}

/**
 * Mirror of the listing-row category so the detail header can show the same badge.

 */
export type GmailMessageDetailCategory =
  (typeof GmailMessageDetailCategory)[keyof typeof GmailMessageDetailCategory];

export const GmailMessageDetailCategory = {
  primary: "primary",
  promotions: "promotions",
  social: "social",
  updates: "updates",
  forums: "forums",
} as const;

/**
 * Single Gmail message with the full body. `htmlBody` is the raw text/html part if present (rendered inside a sandboxed iframe by the UI); `textBody` is the text/plain fallback. `messageIdHeader` and `referencesHeader` surface the original RFC 5322 threading headers so the inline reply composer can construct an `In-Reply-To` / `References` chain when sending the reply via the MailCraft verified-domain pipeline.

 */
export interface GmailMessageDetail {
  id: string;
  threadId: string;
  /** @nullable */
  subject: string | null;
  fromAddress: string;
  /** @nullable */
  fromName: string | null;
  /** @nullable */
  toAddress: string | null;
  receivedAt: string;
  isUnread: boolean;
  snippet: string;
  /** @nullable */
  htmlBody: string | null;
  /** @nullable */
  textBody: string | null;
  /** Mirror of the listing-row category so the detail header can show the same badge.
   */
  category: GmailMessageDetailCategory;
  /**
   * Raw `Message-ID` header value of the original message (including the angle brackets, e.g. `<abc123@mail.example.com>`). Used as `In-Reply-To` on the outgoing reply.

   * @nullable
   */
  messageIdHeader: string | null;
  /**
   * Existing `References` header chain on the original message (space-separated `<id>` tokens). The reply composer appends the original `Message-ID` to this chain when building its own `References` header.

   * @nullable
   */
  referencesHeader: string | null;
  /** Raw Gmail label ids currently applied to the message (e.g. `INBOX`, `UNREAD`, `Label_42`). The label picker uses these to pre-check the user-type labels that are already on the message so unchecking removes them.
   */
  labelIds: string[];
  verificationStatus: InboundVerificationStatus | null;
  verificationDetails: VerificationDetails | null;
}

export type GmailLabelType =
  (typeof GmailLabelType)[keyof typeof GmailLabelType];

export const GmailLabelType = {
  system: "system",
  user: "user",
} as const;

/**
 * A Gmail label entry. `type` distinguishes Google's built-in labels (INBOX, UNREAD, STARRED, ...) from labels the user has created.

 */
export interface GmailLabel {
  id: string;
  name: string;
  type: GmailLabelType;
}

export interface GmailLabelList {
  labels: GmailLabel[];
}

/**
 * Body for `POST /inbox/gmail/labels`. The trimmed `name` is passed verbatim to Gmail's `labels.create`; Gmail enforces uniqueness within the user's mailbox and rejects names with a leading or trailing slash.

 */
export interface GmailCreateLabelInput {
  /**
   * @minLength 1
   * @maxLength 225
   */
  name: string;
}

/**
 * Body for `POST /inbox/gmail/messages/{id}/reply`. `text` is the plain-text body (required); `html` is an optional rich body (when omitted the server generates a minimal `<pre>` wrapper from `text`); `subject` overrides the default `Re: <inbound>`.

 */
export interface GmailReplyInput {
  /**
   * @minLength 1
   * @maxLength 50000
   */
  text: string;
  /** @maxLength 200000 */
  html?: string;
  /** @maxLength 500 */
  subject?: string;
}

export interface GmailSendResult {
  id: string;
  threadId: string;
  labelIds: string[];
}

export interface GmailReadStateInput {
  read: boolean;
}

export interface GmailReadStateResult {
  id: string;
  read: boolean;
}

export interface GmailArchiveResult {
  id: string;
  archived: boolean;
}

/**
 * Add and/or remove labels in one call. At least one of `add` / `remove` must be non-empty.

 */
export interface GmailLabelsMutation {
  /** @maxItems 100 */
  add?: string[];
  /** @maxItems 100 */
  remove?: string[];
}

export interface GmailLabelsMutationResult {
  id: string;
  labelIds: string[];
}

/**
 * Result of `POST /inbox/gmail/watch`. When the server is missing the `GMAIL_PUBSUB_TOPIC` env var (i.e. push isn't wired up yet) the only field returned is `configured: false`. Otherwise all four fields are populated with the active watch's state.

 */
export interface GmailWatchResult {
  configured: boolean;
  gmailAddress?: string | null;
  historyId?: string | null;
  expiresAt?: string | null;
}

export type WebhookEventType =
  (typeof WebhookEventType)[keyof typeof WebhookEventType];

export const WebhookEventType = {
  emaildelivered: "email.delivered",
  emailopened: "email.opened",
  emailclicked: "email.clicked",
  emailbounced: "email.bounced",
  emailcomplained: "email.complained",
  emailreplied: "email.replied",
} as const;

export interface WebhookEndpoint {
  id: number;
  url: string;
  eventTypes: WebhookEventType[];
  active: boolean;
  secretPrefix: string;
  /**
   * Count of permanent-failure outcomes since the last successful delivery. Reset to 0 on every delivered event and on owner-driven re-enable. Crosses an internal threshold to trigger auto-pause.

   * @minimum 0
   */
  consecutiveFailures: number;
  /**
   * Timestamp at which MailCraft auto-paused this endpoint after too many consecutive delivery failures. Null when the endpoint has never been auto-paused (or has been re-enabled by the owner).

   * @nullable
   */
  pausedAt: string | null;
  /**
   * Short human-readable reason explaining why the endpoint was auto-paused (e.g. "Subscriber rejected with HTTP 410"). Null when not paused.

   * @nullable
   */
  pausedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookEndpointEnvelope {
  endpoint: WebhookEndpoint;
}

export interface WebhookEndpointList {
  endpoints: WebhookEndpoint[];
}

export interface CreateWebhookEndpointInput {
  /** @maxLength 2048 */
  url: string;
  /** @minItems 1 */
  eventTypes: WebhookEventType[];
}

/**
 * Returned exactly once on endpoint creation. The `secret` field is the plaintext signing secret and is never re-revealed by the API.

 */
export interface CreateWebhookEndpointResult {
  endpoint: WebhookEndpoint;
  secret: string;
}

export interface UpdateWebhookEndpointInput {
  /** @maxLength 2048 */
  url?: string;
  /** @minItems 1 */
  eventTypes?: WebhookEventType[];
  active?: boolean;
}

export type WebhookDeliveryStatus =
  (typeof WebhookDeliveryStatus)[keyof typeof WebhookDeliveryStatus];

export const WebhookDeliveryStatus = {
  pending: "pending",
  delivered: "delivered",
  failed: "failed",
} as const;

/**
 * JSON payload that was POSTed to the subscriber. This is the same object the delivery worker signs and sends; surfaced so the endpoint owner can inspect what they were supposed to receive when debugging a failed delivery. Null when the delivery was seeded without a payload (e.g. test fixtures).
 * @nullable
 */
export type WebhookDeliveryPayload = { [key: string]: unknown } | null;

export interface WebhookDelivery {
  id: number;
  endpointId: number;
  eventId: string;
  /** The event type for this delivery. Usually one of the subscribable WebhookEventType values (e.g. email.delivered), but may also be webhook.test for synthetic test deliveries.
   */
  eventType: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  /**
   * JSON payload that was POSTed to the subscriber. This is the same object the delivery worker signs and sends; surfaced so the endpoint owner can inspect what they were supposed to receive when debugging a failed delivery. Null when the delivery was seeded without a payload (e.g. test fixtures).
   * @nullable
   */
  payload: WebhookDeliveryPayload;
  /** @nullable */
  lastStatusCode: number | null;
  /** @nullable */
  lastResponseBody: string | null;
  /** @nullable */
  lastError: string | null;
  /** @nullable */
  nextRetryAt: string | null;
  /** @nullable */
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryList {
  deliveries: WebhookDelivery[];
}

export interface WebhookRedeliverResult {
  ok: boolean;
}

export interface WebhookTestResult {
  ok: boolean;
  deliveryId: number;
}

export interface ApiKey {
  id: number;
  label: string;
  secretPrefix: string;
  createdAt: string;
  /** @nullable */
  lastUsedAt: string | null;
  /** @nullable */
  revokedAt: string | null;
}

export interface ApiKeyEnvelope {
  apiKey: ApiKey;
}

export interface ApiKeyList {
  apiKeys: ApiKey[];
}

export interface CreateApiKeyInput {
  /**
   * @minLength 1
   * @maxLength 80
   */
  label: string;
}

/**
 * Returned exactly once on API key creation. The `secret` field is the plaintext bearer token and is never re-revealed by the API.

 */
export interface CreateApiKeyResult {
  apiKey: ApiKey;
  secret: string;
}

export interface RateLimitAlert {
  id: number;
  category: string;
  count: number;
  threshold: number;
  topIps: string[];
  topMessageIds: string[];
  windowStart: string;
  windowEnd: string;
  createdAt: string;
}

export interface RateLimitAlertList {
  alerts: RateLimitAlert[];
  totalCount: number;
}

/**
 * Not signed in
 */
export type UnauthorizedResponse = ErrorResponse;

/**
 * Resource not found
 */
export type NotFoundResponse = ErrorResponse;

/**
 * Action not permitted on this resource
 */
export type ForbiddenResponse = ErrorResponse;

/**
 * Invalid request
 */
export type BadRequestResponse = ErrorResponse;

/**
 * Resource already exists
 */
export type ConflictResponse = ErrorResponse;

export type ListTemplatesParams = {
  category?: string;
  source?: ListTemplatesSource;
};

export type ListTemplatesSource =
  (typeof ListTemplatesSource)[keyof typeof ListTemplatesSource];

export const ListTemplatesSource = {
  builtin: "builtin",
  user: "user",
  all: "all",
} as const;

export type UpdateBrandSettings403 = {
  error: string;
  code?: string;
  /** @nullable */
  managedByEmail?: string | null;
};

export type ListEmailLogsParams = {
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
  status?: ListEmailLogsStatus;
};

export type ListEmailLogsStatus =
  (typeof ListEmailLogsStatus)[keyof typeof ListEmailLogsStatus];

export const ListEmailLogsStatus = {
  queued: "queued",
  sent: "sent",
  failed: "failed",
  all: "all",
} as const;

export type ListEngagementEventsParams = {
  /**
 * Filter the event list to a single kind. `all` (the default) returns both opens and clicks.

 */
  type?: ListEngagementEventsType;
  /**
 * When true (the default), excludes events flagged as likely bot/prefetcher hits (Apple Mail Privacy Protection, GoogleImageProxy, Outlook SafeLinks scanners, …). Mirrors the dashboard's default behavior.

 */
  hideBots?: boolean;
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
  /**
 * ISO-8601 timestamp returned as `nextCursor` from the previous page. Returns events strictly older than this timestamp. Omit on the first page.

 */
  cursor?: string;
  /**
 * ISO-8601 date (YYYY-MM-DD) for the start of the display window (inclusive). When omitted the server falls back to a 90-day lookback from today.

 */
  startDate?: string;
  /**
 * ISO-8601 date (YYYY-MM-DD) for the end of the display window (inclusive). When omitted the upper bound is now.

 */
  endDate?: string;
};

export type ListEngagementEventsType =
  (typeof ListEngagementEventsType)[keyof typeof ListEngagementEventsType];

export const ListEngagementEventsType = {
  all: "all",
  open: "open",
  click: "click",
} as const;

export type ExportEngagementEventsCsvParams = {
  type?: ExportEngagementEventsCsvType;
  hideBots?: boolean;
  /**
 * ISO-8601 date (YYYY-MM-DD) for the start of the export window (inclusive). When omitted the server falls back to a 90-day lookback from today.

 */
  startDate?: string;
  /**
 * ISO-8601 date (YYYY-MM-DD) for the end of the export window (inclusive — events on this date ARE included). When omitted defaults to today. Returns 400 if startDate is after endDate.

 */
  endDate?: string;
};

export type ExportEngagementEventsCsvType =
  (typeof ExportEngagementEventsCsvType)[keyof typeof ExportEngagementEventsCsvType];

export const ExportEngagementEventsCsvType = {
  all: "all",
  open: "open",
  click: "click",
} as const;

export type ListInboundMailboxesParams = {
  /**
 * When true, the response also includes mailboxes whose status is `disabled`. Defaults to false so the common sidebar/brand list only sees mailboxes that currently accept mail.

 */
  includeDisabled?: boolean;
};

export type SearchEmailsParams = {
  /**
   * Free-form search text (websearch syntax). Empty / missing returns the most recent rows for the matching filters.
   */
  q?: string;
  direction?: SearchEmailsDirection;
  dateFrom?: string;
  dateTo?: string;
  /**
   * Filter to sent rows with this delivery status. Ignored for inbound / Gmail buckets. `bounced` and `complained` are accepted for forward-compatibility with future webhook integrations and currently match no rows.
   */
  status?: SearchEmailsStatus;
  /**
   * Filter to sent rows generated from this template. Ignored for inbound / Gmail buckets.
   * @minimum 1
   */
  templateId?: number;
  /**
   * When true, narrows inbound + Gmail rows to those that carry at least one attachment. Sent rows are excluded from the bucket because the schema does not record attachments separately.
   */
  hasAttachment?: boolean;
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
  /**
   * @minimum 0
   */
  offset?: number;
};

export type SearchEmailsDirection =
  (typeof SearchEmailsDirection)[keyof typeof SearchEmailsDirection];

export const SearchEmailsDirection = {
  all: "all",
  sent: "sent",
  inbound: "inbound",
  gmail: "gmail",
} as const;

export type SearchEmailsStatus =
  (typeof SearchEmailsStatus)[keyof typeof SearchEmailsStatus];

export const SearchEmailsStatus = {
  sent: "sent",
  failed: "failed",
  queued: "queued",
  bounced: "bounced",
  complained: "complained",
} as const;

export type ListInboundEmailsParams = {
  /**
   * Filter to a single mailbox by full address (e.g. sales@example.com).
   */
  mailbox?: string;
  read?: ListInboundEmailsRead;
  /**
 * Which slice of the user's inbox to return.
  - `active` (default): all threads except those whose
    snooze is still in the future. Pinned threads sort
    first; unsnoozed-but-not-yet-dismissed threads carry
    a transient `justUnsnoozed=true` flag.
  - `snoozed`: only threads with a snooze still in effect
    (`snoozedUntil > now()`).
  - `pinned`: only pinned threads, sorted most recently
    pinned first.

 */
  view?: ListInboundEmailsView;
  /**
   * Filter threads by the latest message's verification status.
   */
  verification?: ListInboundEmailsVerification;
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
};

export type ListInboundEmailsRead =
  (typeof ListInboundEmailsRead)[keyof typeof ListInboundEmailsRead];

export const ListInboundEmailsRead = {
  read: "read",
  unread: "unread",
  all: "all",
} as const;

export type ListInboundEmailsView =
  (typeof ListInboundEmailsView)[keyof typeof ListInboundEmailsView];

export const ListInboundEmailsView = {
  active: "active",
  snoozed: "snoozed",
  pinned: "pinned",
} as const;

export type ListInboundEmailsVerification =
  (typeof ListInboundEmailsVerification)[keyof typeof ListInboundEmailsVerification];

export const ListInboundEmailsVerification = {
  bimi: "bimi",
  dmarc: "dmarc",
  unverified: "unverified",
  fail: "fail",
} as const;

export type GetInboundVerificationStatsParams = {
  /**
   * Filter to a single mailbox by full address.
   */
  mailbox?: string;
  /**
   * Number of days to look back (1–90, default 7).
   * @minimum 1
   * @maximum 90
   */
  days?: number;
};

export type GetInboundVerificationStatsByMailboxParams = {
  /**
   * Number of days to look back (1–90, default 7).
   * @minimum 1
   * @maximum 90
   */
  days?: number;
};

export type GetInboundVerificationStatsByMailbox200 = {
  days: number;
  mailboxes: MailboxVerificationStats[];
};

export type SetInboundEmailReadState200 = {
  id: number;
  /** @nullable */
  readAt: string | null;
};

export type ListGmailMessagesParams = {
  read?: ListGmailMessagesRead;
  /**
 * Same semantics as `view` on `/inbox/emails`. Snooze, pin and reminder are tracked locally in MailCraft for Gmail threads (we hold only the read-only Gmail scope) so the messages themselves are not modified in Gmail.

 */
  view?: ListGmailMessagesView;
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
};

export type ListGmailMessagesRead =
  (typeof ListGmailMessagesRead)[keyof typeof ListGmailMessagesRead];

export const ListGmailMessagesRead = {
  read: "read",
  unread: "unread",
  all: "all",
} as const;

export type ListGmailMessagesView =
  (typeof ListGmailMessagesView)[keyof typeof ListGmailMessagesView];

export const ListGmailMessagesView = {
  active: "active",
  snoozed: "snoozed",
  pinned: "pinned",
} as const;

export type StopGmailWatch200 = {
  ok: boolean;
};

export type AdminListUsersParams = {
  /**
   * Substring match on email, first name, or last name.
   */
  search?: string;
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
  /**
   * @minimum 0
   */
  offset?: number;
};

export type AdminBulkDeleteUsers200ResultsItem = {
  clerkUserId: string;
  deleted: boolean;
  /** @nullable */
  error: string | null;
};

export type AdminBulkDeleteUsers200 = {
  results: AdminBulkDeleteUsers200ResultsItem[];
};

export type AdminMergeUsers200 = {
  merged: boolean;
  sourceDeleted: boolean;
  message: string;
};

export type AdminListAuditLogParams = {
  /**
   * Filter to events recorded by this local users.id.
   * @minimum 1
   */
  actorUserId?: number;
  /**
   * Exact match on `eventType` (e.g. `template.updated`).
   */
  eventType?: string;
  /**
   * Exact match on `targetType` (e.g. `template`).
   */
  targetType?: string;
  /**
   * Exact match on `targetId` (combine with `targetType` for a single object's history).
   */
  targetId?: string;
  /**
   * Case-insensitive substring match across `description`, `actorEmail`, `eventType`, and `targetType`.
   */
  q?: string;
  /**
   * Only include events at or after this ISO timestamp.
   */
  since?: string;
  /**
   * Only include events strictly before this ISO timestamp.
   */
  until?: string;
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
  /**
   * @minimum 0
   */
  offset?: number;
};

export type AdminGetEmailVolumeParams = {
  /**
   * Inclusive start of the UTC window (YYYY-MM-DD). Defaults to today UTC.
   */
  from?: string;
  /**
   * Inclusive end of the UTC window (YYYY-MM-DD). Defaults to today UTC.
   */
  to?: string;
  /**
   * When true, includes `kind = "test"` sends in the counts.
   */
  includeTest?: boolean;
  /**
 * IANA timezone identifier (e.g. `America/New_York`, `Asia/Singapore`) used to resolve "today" and date-window boundaries in the viewer's local time. When omitted or invalid, dates are bucketed in UTC.

 */
  tz?: string;
};

export type AdminGetEmailVolumeForUserParams = {
  /**
   * @minimum 1
   */
  userId: number;
  from?: string;
  to?: string;
  includeTest?: boolean;
  /**
   * IANA timezone for date-window resolution. Defaults to UTC.
   */
  tz?: string;
};

export type AdminGetEmailVolumeCsvParams = {
  from?: string;
  to?: string;
  includeTest?: boolean;
  /**
   * IANA timezone for date-window resolution. Defaults to UTC.
   */
  tz?: string;
};

export type AdminListRateLimitAlertsParams = {
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
  /**
   * @minimum 0
   */
  offset?: number;
  /**
 * Exact match on category (e.g. `pubsub.rate_limited_ip` or `pubsub.rate_limited_msgid`).

 */
  category?: string;
  /**
   * Only return alerts created at or after this ISO-8601 timestamp.
   */
  since?: string;
  /**
   * Only return alerts created before this ISO-8601 timestamp.
   */
  until?: string;
};

export type AdminListTrashedTemplatesParams = {
  /**
   * Filter results to only trashed templates belonging to this owner (user id).
   * @minimum 1
   */
  ownerId?: number;
  /**
   * Maximum number of items to return per page.
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
  /**
   * Number of items to skip before returning results.
   * @minimum 0
   */
  offset?: number;
};
